import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, apiFetch } from '../lib/api';
import useIsMobile from '../hooks/useIsMobile';
import { extractEventsFromFile } from '../lib/gemini';
import { TravelFormModal } from './Travel';
import { usePeople } from "../context/PeopleContext";
import { Card, Btn, SLabel, PersonBadge, fmt12, fmtDate, todayStr, WeatherIcon } from '../components/ui';
import EmailSearch from '../components/EmailSearch';
import { useConfirm } from '../components/ConfirmModal';
import ReviewAndSendModal from '../components/ReviewAndSendModal';
import PersonBioCard from '../components/PersonBioCard';
import { shortCodeForIana } from '../lib/tz';

// TEAM now comes from PeopleContext

function to12h(v) {
  if (!v || !v.match(/^\d{1,2}:\d{2}$/)) return v;
  const [h, m] = v.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return h12 + ':' + String(m).padStart(2,'0') + ' ' + ampm;
}

function to24h(v) {
  if (!v) return '';
  const match = v.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!match) return '';
  let h = parseInt(match[1]);
  const m = match[2];
  const ampm = match[3]?.toLowerCase();
  if (ampm === 'pm' && h !== 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  return String(h).padStart(2,'0') + ':' + m;
}

function TimePicker({ value, onChange, placeholder = "Set time" }) {
  const display12 = value && value.match(/^\d{1,2}:\d{2}$/) ? to12h(value) : '';
  const [localVal, setLocalVal] = useState(display12);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => { setLocalVal(value && value.match(/^\d{1,2}:\d{2}$/) ? to12h(value) : ''); }, [value]);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropH = 420;
      const spaceBelow = window.innerHeight - rect.bottom - 10;
      let top = spaceBelow >= dropH ? rect.bottom + 6 : rect.top - dropH - 6;
      top = Math.max(10, Math.min(top, window.innerHeight - dropH - 10));
      setPos({
        top,
        left: Math.min(rect.left, window.innerWidth - 290),
      });
    }
    setOpen(v => !v);
  };

  const parsed = value ? (() => { const [h,m]=value.split(':').map(Number); return { h:h%12||12, m:String(m).padStart(2,'0'), ampm:h>=12?'PM':'AM' }; })() : null;
  const [selH, setSelH] = useState(parsed?.h || 9);
  const [selM, setSelM] = useState(parsed?.m || '00');
  const [selAMPM, setSelAMPM] = useState(parsed?.ampm || 'AM');
  useEffect(() => { if (parsed) { setSelH(parsed.h); setSelM(parsed.m); setSelAMPM(parsed.ampm); } }, [value]);

  const select = (h, m, ampm) => {
    let hour = parseInt(h);
    if (ampm==='PM'&&hour!==12) hour+=12;
    if (ampm==='AM'&&hour===12) hour=0;
    onChange(String(hour).padStart(2,'0')+':'+String(m).padStart(2,'0'));
    setOpen(false);
  };

  const hours = Array.from({length:12},(_,i)=>i+1);
  const minutes = ['00','05','10','15','20','25','30','35','40','45','50','55'];

  const pillStyle = (active) => ({
    width:38, height:34, display:'flex', alignItems:'center', justifyContent:'center',
    borderRadius:10, border:'none', cursor:'pointer', fontFamily:'inherit',
    fontSize:13, fontWeight: active ? 700 : 500, transition:'all 0.12s',
    background: active ? 'var(--blue)' : 'transparent',
    color: active ? '#fff' : 'var(--text2)',
    boxShadow: active ? '0 2px 8px rgba(30,64,175,0.3)' : 'none',
  });

  return (
    <div style={{ position:'relative' }} ref={ref}>
      <button ref={btnRef} onClick={handleOpen} type="button" style={{
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
        width:'100%', background:'var(--card)', border:`1.5px solid ${open?'var(--blue)':'var(--border)'}`,
        borderRadius:12, padding:'11px 16px', cursor:'pointer', fontFamily:'inherit',
        fontSize:15, fontWeight:600, color: value ? 'var(--text)' : 'var(--text3)', transition:'border-color 0.15s',
      }}>
        <span>{display12 || placeholder}</span>
        <i className="fa-regular fa-clock" style={{ fontSize:13, color:'var(--blue)' }}></i>
      </button>

      {open && (
        <div style={{
          position:'fixed', top:pos.top, left:pos.left, zIndex:9999,
          width:280,
          background:'var(--card)', border:'1px solid var(--border)', borderRadius:16,
          padding:'16px 14px 12px',
          boxShadow:'0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
          backdropFilter:'blur(20px)',
        }}>
          {/* Type-in field */}
          <div style={{ marginBottom:12 }}>
            <input
              value={localVal}
              onChange={e => setLocalVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const t = localVal.trim();
                  const m = t.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
                  if (m) {
                    let h = parseInt(m[1]);
                    const min = m[2];
                    const ap = m[3] ? m[3].toUpperCase() : (h >= 12 ? 'PM' : 'AM');
                    if (ap === 'PM' && h !== 12) h += 12;
                    if (ap === 'AM' && h === 12) h = 0;
                    onChange(String(h).padStart(2,'0')+':'+min);
                    setOpen(false);
                  }
                }
              }}
              placeholder="Type time (e.g. 7:30 AM)"
              style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10,
                padding:'10px 14px', fontSize:15, fontWeight:600, color:'var(--text)', textAlign:'center',
                outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}
            />
          </div>

          {/* Preview */}
          <div style={{ textAlign:'center', marginBottom:14 }}>
            <span style={{ fontSize:28, fontWeight:800, color:'var(--text)', letterSpacing:-1, fontFamily:'inherit' }}>
              {String(selH).padStart(2,'0')}:{selM}
            </span>
            <span style={{ fontSize:14, fontWeight:600, color:'var(--blue)', marginLeft:6 }}>{selAMPM}</span>
          </div>

          {/* Hour */}
          <p style={{ fontSize:10, fontWeight:700, color:'var(--text3)', marginBottom:6, letterSpacing:1.5 }}>HOUR</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:3, marginBottom:12 }}>
            {hours.map(h => (
              <button key={h} type="button" onClick={() => setSelH(h)} style={pillStyle(selH===h)}>{h}</button>
            ))}
          </div>

          {/* Minute */}
          <p style={{ fontSize:10, fontWeight:700, color:'var(--text3)', marginBottom:6, letterSpacing:1.5 }}>MINUTE</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:3, marginBottom:12 }}>
            {minutes.map(m => (
              <button key={m} type="button" onClick={() => setSelM(m)} style={pillStyle(selM===m)}>{m}</button>
            ))}
          </div>

          {/* AM/PM */}
          <div style={{ display:'flex', gap:4, marginBottom:14, background:'rgba(255,255,255,0.03)', borderRadius:10, padding:3 }}>
            {['AM','PM'].map(ap => (
              <button key={ap} type="button" onClick={() => setSelAMPM(ap)}
                style={{ flex:1, padding:'8px', borderRadius:8, border:'none', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', transition:'all 0.12s',
                  background: selAMPM===ap ? 'var(--blue)' : 'transparent',
                  color: selAMPM===ap ? '#fff' : 'var(--text3)',
                  boxShadow: selAMPM===ap ? '0 2px 6px rgba(30,64,175,0.3)' : 'none' }}>
                {ap}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display:'flex', gap:8 }}>
            <button type="button" onClick={() => { onChange(''); setLocalVal(''); setOpen(false); }}
              style={{ flex:1, padding:'9px', borderRadius:10, border:'1px solid var(--border)', background:'transparent', color:'var(--text3)', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              Clear
            </button>
            <button type="button" onClick={() => select(selH, selM, selAMPM)}
              style={{ flex:2, padding:'9px', borderRadius:10, border:'none', background:'var(--blue)', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', boxShadow:'0 2px 8px rgba(30,64,175,0.3)' }}>
              Set {String(selH).padStart(2,'0')}:{selM} {selAMPM}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CalendarPicker({ value, onChange, compact }) {
  const safeDate = value || todayStr();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [vy, setVy] = useState(new Date(safeDate+'T12:00:00').getFullYear());
  const [vm, setVm] = useState(new Date(safeDate+'T12:00:00').getMonth());
  const ref = useRef(null);
  const btnRef = useRef(null);

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const firstDay = new Date(vy, vm, 1).getDay();
  const daysInMonth = new Date(vy, vm+1, 0).getDate();
  const cells = [...Array(firstDay).fill(null), ...Array.from({length:daysInMonth},(_,i)=>i+1)];
  while(cells.length%7!==0) cells.push(null);
  const today = todayStr();

  const prevM = () => vm===0?(setVy(y=>y-1),setVm(11)):setVm(m=>m-1);
  const nextM = () => vm===11?(setVy(y=>y+1),setVm(0)):setVm(m=>m+1);
  const goToday = () => { const d = new Date(); setVy(d.getFullYear()); setVm(d.getMonth()); onChange(todayStr()); setOpen(false); };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Sync view when value changes externally
  useEffect(() => {
    if (value) { const d = new Date(value+'T12:00:00'); setVy(d.getFullYear()); setVm(d.getMonth()); }
  }, [value]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const dropH = 340;
      const dropW = compact ? 280 : 320;
      const spaceBelow = window.innerHeight - rect.bottom - 10;
      let top = spaceBelow >= dropH ? rect.bottom + 6 : rect.top - dropH - 6;
      top = Math.max(10, Math.min(top, window.innerHeight - dropH - 10));
      setPos({
        top,
        left: Math.min(rect.left, window.innerWidth - dropW - 10),
      });
    }
    setOpen(v => !v);
  };

  const sz = compact ? 32 : 36;

  return (
    <div style={{ position:'relative' }} ref={ref}>
      <button ref={btnRef} onClick={handleOpen} style={{
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
        width:'100%', background:'var(--card)', border:`1.5px solid ${open?'var(--blue)':'var(--border)'}`,
        borderRadius:12, padding: compact ? '8px 12px' : '11px 16px', cursor:'pointer', fontFamily:'inherit',
        fontSize: compact ? 13 : 15, fontWeight:600, color:'var(--text)', transition:'border-color 0.15s',
      }}>
        <span>{fmtDate(value, {weekday:'short', month:'long', day:'numeric', year:'numeric'})}</span>
        <i className={`fa-solid fa-chevron-${open?'up':'down'}`} style={{ fontSize:10, color:'var(--blue)', transition:'transform 0.2s' }}></i>
      </button>

      {open && (
        <div style={{
          position:'fixed', top:pos.top, left:pos.left, zIndex:9999,
          width: compact ? 280 : 320,
          background:'var(--card)', border:'1px solid var(--border)',
          borderRadius:16, padding:'16px 14px 12px',
          boxShadow:'0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
          backdropFilter:'blur(20px)',
        }}>
          {/* Month/Year nav */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, padding:'0 2px' }}>
            <button onClick={prevM} style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.04)', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:12, transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(30,64,175,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button onClick={goToday} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:'4px 8px', borderRadius:6 }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <span style={{ fontSize:15, fontWeight:800, color:'var(--text)', letterSpacing:-0.3 }}>{MONTHS[vm]}</span>
              <span style={{ fontSize:15, fontWeight:400, color:'var(--text2)', marginLeft:5 }}>{vy}</span>
            </button>
            <button onClick={nextM} style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(255,255,255,0.04)', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:12, transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(30,64,175,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>

          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
            {['S','M','T','W','T','F','S'].map((d,i) => (
              <div key={i} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:'var(--text3)', padding:'4px 0', letterSpacing:0.5 }}>{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
            {cells.map((day,i) => {
              if (!day) return <div key={i} style={{ height:sz }} />;
              const ds = `${vy}-${String(vm+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isSel = ds === value;
              const isToday = ds === today;
              const isPast = ds < today;
              return (
                <button key={i} onClick={() => { onChange(ds); setOpen(false); }}
                  style={{
                    width:sz, height:sz, display:'flex', alignItems:'center', justifyContent:'center',
                    borderRadius:'50%', border:'none', cursor:'pointer', fontFamily:'inherit',
                    position:'relative', transition:'all 0.12s',
                    background: isSel ? 'var(--blue)' : 'transparent',
                    fontSize:13, fontWeight: isSel||isToday ? 700 : 400,
                    color: isSel ? '#fff' : isPast ? 'var(--text3)' : 'var(--text)',
                    boxShadow: isSel ? '0 2px 8px rgba(30,64,175,0.35)' : 'none',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background='rgba(255,255,255,0.06)'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background='transparent'; }}>
                  {day}
                  {isToday && !isSel && <span style={{ position:'absolute', bottom:2, left:'50%', transform:'translateX(-50%)', width:4, height:4, borderRadius:'50%', background:'var(--blue)' }}></span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Hardcoded name aliases and initials
const KNOWN_ALIASES = {
  'paul kevin jonas ii': 'Kevin Jonas', 'paul kevin jonas': 'Kevin Jonas',
  'paul jonas': 'Kevin Jonas', 'kevin jonas ii': 'Kevin Jonas', 'kevin jonas': 'Kevin Jonas',
};
const KNOWN_INITIALS = { 'kevin jonas': 'K2', 'kevin jonas ii': 'K2' };

function resolveInitials(passengerName, artists) {
  if (!passengerName) return '?';
  const lower = passengerName.toLowerCase().trim();
  const suffixes = new Set(['ii','iii','iv','v','jr','jr.','sr','sr.','esq','esq.']);
  const cleanLower = lower.split(' ').filter(w => !suffixes.has(w)).join(' ');
  // Check hardcoded overrides
  const resolved = KNOWN_ALIASES[lower] || KNOWN_ALIASES[cleanLower] || null;
  if (resolved && KNOWN_INITIALS[resolved.toLowerCase()]) return KNOWN_INITIALS[resolved.toLowerCase()];
  if (KNOWN_INITIALS[lower] || KNOWN_INITIALS[cleanLower]) return KNOWN_INITIALS[lower] || KNOWN_INITIALS[cleanLower];
  // Check artist aliases
  if (artists?.length) {
    for (const a of artists) {
      const matched = (a.aliases || []).some(al => { const al2 = al.toLowerCase().trim(); return al2 === lower || al2 === cleanLower; })
        || a.name.toLowerCase().trim() === lower || a.name.toLowerCase().trim() === cleanLower;
      if (!matched) {
        // Fuzzy: passenger contains artist's first AND last name
        const aParts = a.name.toLowerCase().split(' ').filter(Boolean);
        if (aParts.length >= 2 && cleanLower.split(' ').includes(aParts[0]) && cleanLower.split(' ').includes(aParts[aParts.length-1])) {
          if (KNOWN_INITIALS[a.name.toLowerCase()]) return KNOWN_INITIALS[a.name.toLowerCase()];
          const parts = a.name.split(' ').filter(Boolean);
          return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
        }
        continue;
      }
      if (KNOWN_INITIALS[a.name.toLowerCase()]) return KNOWN_INITIALS[a.name.toLowerCase()];
      const parts = a.name.split(' ').filter(Boolean);
      return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
    }
  }
  // Default: first + last initial
  const parts = passengerName.split(' ').filter(p => p && !suffixes.has(p.toLowerCase()));
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
}

// Travel chip subtitle: artist initials only, matching the iOS chip's
// `personInitials` (single set, no passenger fan-out). Lead passenger
// wins; falls back to the first name in the comma list if no lead set.
function flightInitials(passengerStr, leadPassenger, artists) {
  const pax = (passengerStr || '').split(',').map(p => p.trim()).filter(Boolean);
  const lead = leadPassenger || pax[0] || '';
  if (!lead) return '?';
  return resolveInitials(lead, artists);
}

function findPersonColor(colorMap, name) {
  if (!name) return '#e6edf3';
  // Exact match first
  if (colorMap[name]) return colorMap[name];
  // Case-insensitive match
  const lower = name.toLowerCase();
  const key = Object.keys(colorMap).find(k => k.toLowerCase() === lower);
  if (key) return colorMap[key];
  // Partial match (first name matches)
  const firstName = lower.split(' ')[0];
  const partial = Object.keys(colorMap).find(k => k.toLowerCase().split(' ')[0] === firstName);
  if (partial) return colorMap[partial];
  return '#e6edf3';
}

function EventRow({ event, onEdit, onDelete }) {
  const { colorMap: PERSON_COLORS } = usePeople();
  const firstPerson = event.person?.split(",")[0]?.trim();
  const pc = findPersonColor(PERSON_COLORS, firstPerson);
  const isW = pc === '#e6edf3';
  return (
    <div style={{ display:'flex', alignItems:'stretch', background:'var(--card)', borderRadius:10, border:'1px solid var(--border)', overflow:'hidden', marginBottom:8 }}>
      <div style={{ width:4, background: isW?'var(--border2)':pc, flexShrink:0 }} />
      <div style={{ flex:1, padding:'10px 14px' }}>
        <p style={{ fontSize:15, fontWeight:800, color: !isW ? pc : 'var(--text)', margin:0, lineHeight:1.4 }}>
          {event.timeStart ? <><span style={{ textDecoration:'underline' }}>{fmt12(event.timeStart)}</span>: {event.title}</> : event.title}
        </p>
        {event.notes && <p style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>{event.notes}</p>}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:4, padding:'10px 10px 10px 0', justifyContent:'center' }}>
        <button onClick={() => onEdit(event)} style={{ fontSize:13, color:'var(--text2)', background:'none', border:'none', cursor:'pointer', padding:'3px 6px' }}><i className="fa-solid fa-pen"></i></button>
        <button onClick={() => onDelete(event.id)} style={{ fontSize:13, color:'var(--red)', background:'none', border:'none', cursor:'pointer', padding:'3px 6px' }}>✕</button>
      </div>
    </div>
  );
}

function EventModal({ event, onSave, onSaveAndSync, onDuplicate, onDelete, onClose, sheetArtistId, sheetDate }) {
  const { team: TEAM, colorMap: PERSON_COLORS, people: artistList } = usePeople();
  const [confirmAction, ConfirmDialog] = useConfirm();
  const [title, setTitle]     = useState(event?.title || '');
  const [selectedPeople, setSelectedPeople] = useState(event?.person ? event.person.split(",").map(p=>p.trim()).filter(Boolean) : []);
  const [date,   setDate]     = useState(event?.date || sheetDate || todayStr());
  const [tStart, setTStart]   = useState(event?.timeStart || '');
  const [tz,     setTz]       = useState(event?.timezone || '');
  const [tEnd,   setTEnd]     = useState(event?.timeEnd || '');
  const [loc,    setLoc]      = useState(event?.location || '');
  // When the location autocomplete returns a place, we resolve it to an
  // IANA tz via /places/details. Used to either auto-fill the tz
  // (cleanest path: admin never touches tz at all) or surface a "tz
  // mismatch — set to PT?" prompt when the picked place's zone differs
  // from what the admin already chose.
  const [tzHint, setTzHint] = useState(null); // null | { short, fromCity }
  const handleLocationPick = useCallback(async (addr, placeId) => {
    setLoc(addr);
    if (!placeId) { setTzHint(null); return; }
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('ds_token');
      const r = await fetch(`${API_URL}/places/details?placeId=${encodeURIComponent(placeId)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await r.json();
      const short = data?.iana ? (shortCodeForIana(data.iana) || data.iana) : null;
      if (!short) { setTzHint(null); return; }
      // Auto-tag when the field is empty — the cleanest path. The
      // admin sets a location, the tz comes for free.
      setTz(prev => {
        if (!prev) { setTzHint(null); return short; }
        // Already-set tz that matches → nothing to do.
        if (prev === short) { setTzHint(null); return prev; }
        // Mismatch → surface the hint and let the admin tap to apply.
        const fromCity = (addr || '').split(',').slice(-3, -2)[0]?.trim() || null;
        setTzHint({ short, fromCity });
        return prev;
      });
    } catch {
      // Non-fatal: fall back to manual tz selection.
      setTzHint(null);
    }
  }, []);
  const [notes,  setNotes]    = useState(event?.notes || '');
  const notesRef               = useRef(null);
  // Insert a "LABEL: " prefix on a new line in the notes textarea so the
  // distribution email's row parser picks it up as a structured detail
  // (DRIVER / GREETER / PAX / NOTE) instead of a plain prose line.
  const insertNoteLabel = (label) => {
    setNotes(prev => {
      const trimmed = (prev || '').replace(/\s+$/, '');
      const sep = trimmed ? '\n' : '';
      return label === '*'
        ? `${trimmed}${sep}*`
        : `${trimmed}${sep}${label}: `;
    });
    setTimeout(() => {
      const el = notesRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 0);
  };
  // Parse notes the same way the distribution email does, so the live
  // preview pane below shows exactly what recipients will see. Mirrors
  // LABEL_RE / renderDetailRows in lib/emailTemplate.js — keep in sync.
  // Case-insensitive label match + uppercase render so GCAL-imported
  // descriptions ("Driver: Mike") and Holly-style notes ("DRIVER: Mike")
  // both come out as bold uppercase rows.
  const NOTES_LABEL_RE = /^([A-Za-z][^:]{0,40}):\s*(.*)$/;
  const previewRows = (notes || '').split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    if (line.startsWith('*')) return { kind: 'italic', text: line };
    const m = line.match(NOTES_LABEL_RE);
    return m ? { kind: 'label', label: m[1].toUpperCase(), value: m[2] } : { kind: 'plain', text: line };
  });
  const [url,    setUrl]      = useState(event?.url || '');
  const [files,  setFiles]     = useState(event?.files || []);
  const [contacts, setContacts] = useState(event?.contacts || []);
  const [travelType, setTravelType] = useState(event?.travelType || null);
  const [calendars, setCalendars] = useState([]);
  const [selCalendar, setSelCalendar] = useState('');
  const [showRepeat, setShowRepeat] = useState(false);
  const [repeatType, setRepeatType] = useState('daily');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [repeatDays, setRepeatDays] = useState([]);
  const [repeating, setRepeating] = useState(false);

  useEffect(() => {
    if (onSaveAndSync && sheetArtistId) {
      api.getCalendars(sheetArtistId).then(d => {
        const allCals = d.calendars || [];
        // Only show calendars the artist has selected (not all Google calendars)
        const selectedIds = d.selectedIds || [];
        const cals = selectedIds.length > 0 ? allCals.filter(c => c.selected || selectedIds.includes(c.id)) : allCals;
        setCalendars(cals);
        if (cals.length) setSelCalendar(cals[0].id);
      }).catch(() => {});
    }
  }, [onSaveAndSync, sheetArtistId]);

  useEffect(() => {
    setTitle(event?.title||''); setSelectedPeople(event?.person ? event.person.split(",").map(p=>p.trim()) : []);
    setDate(event?.date||sheetDate||todayStr()); setTStart(event?.timeStart||''); setTEnd(event?.timeEnd||'');
    setLoc(event?.location||''); setNotes(event?.notes||''); setUrl(event?.url||'');
    setTravelType(event?.travelType || null);
  }, [event]);

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ title, person: selectedPeople.length ? selectedPeople.join(",") : null, date, timeStart:tStart||null, timeEnd:tEnd||null, timezone:tz||null, location:loc||null, notes:notes||null, url:url||null, files, contacts, travelType: travelType || null });
  };

  // Travel-type options — mirror of TravelTypeOption on iOS so events
  // flagged here render with the same badge / hero-strip chip there.
  const TRAVEL_TYPES = [
    { value: 'flight',     label: 'Flight',      icon: 'fa-plane' },
    { value: 'hotel',      label: 'Hotel',       icon: 'fa-bed' },
    { value: 'carservice', label: 'Car Service', icon: 'fa-car-side' },
    { value: 'carrental',  label: 'Car Rental',  icon: 'fa-key' },
    { value: 'ground',     label: 'Ground',      icon: 'fa-van-shuttle' },
    { value: 'train',      label: 'Train',       icon: 'fa-train' },
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <button onClick={onClose}
        style={{ position:'absolute', top:16, left:16, width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.12)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2010 }}>
        <i className="fa-solid fa-xmark" style={{ fontSize:16, color:'#fff' }}></i>
      </button>
      <div style={{ background:'var(--bg2)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:560, padding:'28px 28px calc(28px + env(safe-area-inset-bottom, 0px))', maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <h3 style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{event ? 'Edit Event' : 'Add Event'}</h3>
          <button onClick={onClose} style={{ width:32,height:32,borderRadius:16,background:'var(--bg3)',border:'none',cursor:'pointer',fontSize:14,color:'var(--text2)' }}>✕</button>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <SLabel>Title</SLabel>
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Event title"
              style={{ width:'100%',background:'var(--card)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px',fontSize:15,color:'var(--text)',outline:'none' }} autoFocus />
          </div>
          <div>
            <SLabel>Date</SLabel>
            <CalendarPicker value={date} onChange={setDate} />
          </div>
          <div>
            <SLabel>Who</SLabel>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:8 }}>
              {TEAM.map(p => {
                // Case- and whitespace-insensitive match so a chip
                // whose label differs in case from the saved
                // event.person still toggles cleanly. Without this,
                // an event saved as "alena" wouldn't pair with the
                // "Alena" chip and the chip would never deselect.
                const norm = (x) => (x || '').trim().toLowerCase();
                const sel = selectedPeople.some(x => norm(x) === norm(p));
                const pc2 = PERSON_COLORS[p]||'#e6edf3'; const isW2 = pc2==='#e6edf3';
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSelectedPeople(prev => {
                      const matches = prev.some(x => norm(x) === norm(p));
                      return matches
                        ? prev.filter(x => norm(x) !== norm(p))
                        : [...prev, p];
                    })}
                    style={{
                      display:'flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,
                      background:sel?(isW2?'rgba(230,237,243,0.1)':pc2+'18'):'transparent',
                      border:`1px solid ${sel?(isW2?'var(--border2)':pc2):'var(--border)'}`,
                      cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700,
                      color:sel?(isW2?'var(--text)':pc2):'var(--text2)',
                    }}
                  >
                    <span style={{width:7,height:7,borderRadius:'50%',background:isW2?'rgba(230,237,243,0.4)':pc2}} />
                    {p}
                  </button>
                );
              })}

            </div>
          </div>
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <div>
                <SLabel>Travel event</SLabel>
                <p style={{ fontSize:11, color:'var(--text3)', margin:0 }}>
                  Flips this event to a travel row so it gets the right badge and shows in the hero strip.
                </p>
              </div>
              <button type="button"
                onClick={() => setTravelType(travelType ? null : 'flight')}
                aria-pressed={!!travelType}
                style={{ flexShrink:0, width:44, height:26, borderRadius:13, border:'none', cursor:'pointer',
                  background: travelType ? 'var(--blue)' : 'var(--bg3)',
                  position:'relative', transition:'background 0.15s' }}>
                <span style={{ position:'absolute', top:3, left: travelType ? 21 : 3, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.15s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
              </button>
            </div>
            {travelType && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10 }}>
                {TRAVEL_TYPES.map(t => {
                  const sel = travelType === t.value
                    || (t.value === 'flight' && (travelType === 'flight_departure' || travelType === 'flight_arrival'));
                  return (
                    <button key={t.value} type="button"
                      onClick={() => setTravelType(t.value)}
                      style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:20, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
                        background: sel ? 'rgba(30,64,175,0.15)' : 'transparent',
                        border: `1px solid ${sel ? 'var(--blue)' : 'var(--border)'}`,
                        color: sel ? 'var(--blue)' : 'var(--text2)' }}>
                      <i className={`fa-solid ${t.icon}`} style={{ fontSize:11 }}></i>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <SLabel>Start Time</SLabel>
              <TimePicker value={tStart} onChange={setTStart} placeholder="Start time" pickerId="tStart" />
            </div>
            <div>
              <SLabel>End Time</SLabel>
              <TimePicker value={tEnd} onChange={setTEnd} placeholder="End time" pickerId="tEnd" />
            </div>
          </div>
          <div>
            <SLabel>Timezone <span style={{fontWeight:400,color:'var(--text3)',fontSize:11}}>(optional override)</span></SLabel>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {['ET','CT','MT','PT','GMT','CET','JST','AEST'].map(t => (
                <button key={t} type="button" onClick={() => { setTz(tz===t ? '' : t); setTzHint(null); }}
                  style={{ padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700,
                    background: tz===t ? 'rgba(79,156,249,0.15)' : 'var(--bg3)',
                    borderColor: tz===t ? 'var(--blue)' : 'var(--border)',
                    color: tz===t ? 'var(--blue)' : 'var(--text2)' }}>
                  {t}
                </button>
              ))}
            </div>
            {tzHint && (
              <div style={{
                marginTop:8, padding:'8px 12px', borderRadius:8,
                background:'rgba(245,158,11,0.10)', border:'1px solid rgba(245,158,11,0.4)',
                display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
                fontSize:12, color:'#b45309',
              }}>
                <span>
                  Location is in <strong>{tzHint.short}</strong>
                  {tzHint.fromCity ? <> ({tzHint.fromCity})</> : null} — event tz is set to <strong>{tz}</strong>.
                </span>
                <button type="button"
                  onClick={() => { setTz(tzHint.short); setTzHint(null); }}
                  style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #b45309', background:'transparent', color:'#b45309', cursor:'pointer', fontWeight:700, fontSize:11 }}>
                  Set to {tzHint.short}
                </button>
              </div>
            )}
          </div>
          <div>
            <SLabel>Location</SLabel>
            <LocationSearch value={loc} onChange={handleLocationPick} />
          </div>
          <div>
            <SLabel>Link / URL</SLabel>
            <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://zoom.us/j/..." style={{ width:"100%", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", fontSize:14, color:"var(--text)", outline:"none", boxSizing:"border-box" }} />
          </div>
          <div>
            <SLabel>Notes</SLabel>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
              {[
                { label:'DRIVER',  hint:'Driver name + phone' },
                { label:'GREETER', hint:'Greeter name + phone' },
                { label:'PAX',     hint:'Passenger list' },
                { label:'NOTE',    hint:'Inline note line' },
              ].map(c => (
                <button key={c.label} type="button" title={c.hint} onClick={() => insertNoteLabel(c.label)}
                  style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:999, padding:'4px 10px', fontSize:11, fontWeight:700, letterSpacing:0.4, color:'var(--text2)', cursor:'pointer', fontFamily:'inherit' }}>
                  + {c.label}
                </button>
              ))}
              <button type="button" title="Italic helper note (e.g. *Kevin to JFK: 1h 5m)" onClick={() => insertNoteLabel('*')}
                style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:999, padding:'4px 10px', fontSize:11, fontWeight:700, letterSpacing:0.4, color:'var(--text3)', fontStyle:'italic', cursor:'pointer', fontFamily:'inherit' }}>
                + *italic
              </button>
            </div>
            <textarea ref={notesRef} value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder={"DRIVER: Mike 555-1234\nGREETER: Holly 555-5678\nPAX: K2 (1A), JJ (2D)\n*Italic helper note"}
              rows={4} style={{ width:'100%',background:'var(--card)',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px',fontSize:14,color:'var(--text)',outline:'none',resize:'vertical',fontFamily:'inherit' }} />
            <div style={{ fontSize:11, color:'var(--text3)', marginTop:6, lineHeight:1.5 }}>
              Lines starting with <strong>LABEL:</strong> render as bold rows in the email. Lines starting with <em>*</em> render in italics.
            </div>
            {/* Pillar 1: auto-bio surface — renders only when the
                title looks like it references a person ("Zoom with X",
                "Interview — X", etc.). Bio fetch is opt-in inside the
                card so we don't burn Claude tokens on every modal open. */}
            <PersonBioCard title={title} notes={notes} />
            {(() => {
              // Live preview — mirrors what the email will render, with the
              // label color matching the event's first selected person (so
              // the admin sees the exact color recipients will see).
              const firstP = selectedPeople[0];
              const previewLabelColor = firstP
                ? (findPersonColor(PERSON_COLORS, firstP) || '#1E40AF')
                : '#333333';
              const empty = previewRows.length === 0;
              return (
                <div style={{ marginTop:18, background:'#ffffff', border:'1px solid var(--border)', borderRadius:10, padding: empty ? '20px' : '14px 16px', position:'relative' }}>
                  <div style={{ position:'absolute', top:-8, left:12, background:'var(--card)', color:'var(--text3)', fontSize:9, fontWeight:800, letterSpacing:1.5, padding:'0 8px' }}>EMAIL PREVIEW</div>
                  {empty ? (
                    <div style={{ color:'#94a3b8', fontSize:12, fontStyle:'italic', textAlign:'center' }}>
                      No notes yet — preview will appear here.
                    </div>
                  ) : previewRows.map((r, i) => {
                    const mt = i === 0 ? 0 : 4;
                    if (r.kind === 'italic') return <div key={i} style={{ fontSize:12, color:'#888', fontStyle:'italic', lineHeight:1.5, marginTop:mt }}>{r.text}</div>;
                    if (r.kind === 'label')  return <div key={i} style={{ fontSize:13, color:'#333', lineHeight:1.6, marginTop:mt }}><strong style={{ color: previewLabelColor }}>{r.label}:</strong> {r.value}</div>;
                    return <div key={i} style={{ fontSize:13, color:'#555', lineHeight:1.5, marginTop:mt }}>{r.text}</div>;
                  })}
                </div>
              );
            })()}
          </div>
          <div>
            <SLabel>Contacts</SLabel>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {contacts.map((c, i) => (
                <div key={i} style={{ display:'flex', gap:6 }}>
                  <input value={c.name} onChange={e => setContacts(prev => prev.map((x,j)=>j===i?{...x,name:e.target.value}:x))}
                    placeholder="Name / Role"
                    style={{ flex:1, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text)', outline:'none' }} />
                  <input value={c.phone} onChange={e => setContacts(prev => prev.map((x,j)=>j===i?{...x,phone:e.target.value}:x))}
                    placeholder="Phone number"
                    style={{ flex:1, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 10px', fontSize:13, color:'var(--text)', outline:'none' }} />
                  <button onClick={() => setContacts(prev => prev.filter((_,j)=>j!==i))}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:16, padding:'0 6px' }}>✕</button>
                </div>
              ))}
              <button onClick={() => setContacts(prev => [...prev, { name:'', phone:'' }])}
                style={{ display:'flex', alignItems:'center', gap:6, background:'var(--bg3)', border:'1px dashed var(--border)', borderRadius:8, padding:'8px 12px', cursor:'pointer', fontSize:13, color:'var(--text3)', fontFamily:'inherit' }}>
                ＋ Add contact
              </button>
            </div>
          </div>
          <div>
            <SLabel>Attachments</SLabel>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {files.map((f, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--bg3)', borderRadius:8, padding:'8px 12px' }}>
                  <span style={{ fontSize:13, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'80%' }}>
                    {f.type?.includes('pdf') ? <i className="fa-solid fa-file-pdf"></i> : f.type?.startsWith('image/') ? <i className="fa-solid fa-image"></i> : <i className="fa-solid fa-paperclip"></i>} {f.name}
                  </span>
                  <button onClick={() => setFiles(prev => prev.filter((_,j)=>j!==i))} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:16 }}>✕</button>
                </div>
              ))}
              {(() => {
                const [draggingFile, setDraggingFile] = React.useState(false);
                const handleDrop = async (e) => {
                  e.preventDefault(); setDraggingFile(false);
                  for (const file of Array.from(e.dataTransfer.files)) {
                    const reader = new FileReader();
                    const dataUrl = await new Promise(r => { reader.onload = () => r(reader.result); reader.readAsDataURL(file); });
                    try {
                      const res = await api.uploadFile({ name: file.name, type: file.type, data: dataUrl });
                      setFiles(prev => [...prev, { name: res.name, type: res.type, url: res.url }]);
                    } catch { setFiles(prev => [...prev, { name: file.name, type: file.type, data: dataUrl }]); }
                  }
                };
                const handleChange = async (e) => {
                  for (const file of Array.from(e.target.files)) {
                    const reader = new FileReader();
                    const dataUrl = await new Promise(r => { reader.onload = () => r(reader.result); reader.readAsDataURL(file); });
                    try {
                      const res = await api.uploadFile({ name: file.name, type: file.type, data: dataUrl });
                      setFiles(prev => [...prev, { name: res.name, type: res.type, url: res.url }]);
                    } catch { setFiles(prev => [...prev, { name: file.name, type: file.type, data: dataUrl }]); }
                  }
                  e.target.value = '';
                };
                return (
                  <label
                    onDragOver={e => { e.preventDefault(); setDraggingFile(true); }}
                    onDragLeave={() => setDraggingFile(false)}
                    onDrop={handleDrop}
                    style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6,
                      background: draggingFile ? 'rgba(79,156,249,0.08)' : 'var(--bg3)',
                      border: `2px dashed ${draggingFile ? 'var(--blue)' : 'var(--border)'}`,
                      borderRadius:10, padding:'18px 14px', cursor:'pointer', transition:'all 0.15s' }}>
                    <i className="fa-solid fa-paperclip" style={{ fontSize:24 }}></i>
                    <span style={{ fontSize:13, color: draggingFile ? 'var(--blue)' : 'var(--text3)', fontWeight:600 }}>
                      {draggingFile ? 'Drop to attach' : 'Drop files or click to browse'}
                    </span>
                    <span style={{ fontSize:11, color:'var(--text3)' }}>PDF, images, and more</span>
                    <input type="file" multiple style={{ display:'none' }} onChange={handleChange} />
                  </label>
                );
              })()}
            </div>
          </div>
          {onSaveAndSync && calendars.length > 0 && (
            <div>
              <SLabel>Google Calendar</SLabel>
              <select value={selCalendar} onChange={e => setSelCalendar(e.target.value)}
                style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', colorScheme:'dark' }}>
                {calendars.map(c => (
                  <option key={c.id} value={c.id}>{c.primary ? 'Primary Calendar' : c.name}</option>
                ))}
              </select>
            </div>
          )}
          {event && onDuplicate && (
            <Btn variant="ghost" size="lg" onClick={() => { if (!title.trim()) return; onDuplicate({ ...event, title, person: selectedPeople.length ? selectedPeople.join(",") : null, timeStart:tStart||null, timeEnd:tEnd||null, timezone:tz||null, location:loc||null, notes:notes||null, url:url||null, files, contacts, travelType: travelType || null }, date); }} disabled={!title.trim()} style={{ width:'100%', marginBottom:8 }}>
              Duplicate to {date !== event.date ? new Date(date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : 'same date'}
            </Btn>
          )}
          {/* Repeat / Recurring */}
          {event?.id && !event.isNew && (
            <div style={{ marginBottom:8 }}>
              <button onClick={() => setShowRepeat(v => !v)}
                style={{ width:'100%', padding:'10px 16px', borderRadius:10, border:'1px solid var(--border)', background: showRepeat ? 'rgba(30,64,175,0.06)' : 'var(--bg3)', cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700, color: showRepeat ? 'var(--blue)' : 'var(--text2)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span><i className="fa-solid fa-repeat" style={{ marginRight:8, fontSize:11 }}></i>Repeat this event</span>
                <i className={`fa-solid fa-chevron-${showRepeat ? 'up' : 'down'}`} style={{ fontSize:10 }}></i>
              </button>
              {showRepeat && (
                <div style={{ padding:'14px 16px', border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 10px 10px', background:'var(--bg3)' }}>
                  <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
                    {[['daily','Every day'],['weekdays','Weekdays'],['weekly','Weekly'],['custom','Custom']].map(([val,label]) => (
                      <button key={val} onClick={() => setRepeatType(val)}
                        style={{ padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:700,
                          background: repeatType===val ? 'rgba(30,64,175,0.12)' : 'transparent',
                          borderColor: repeatType===val ? 'var(--blue)' : 'var(--border)',
                          color: repeatType===val ? 'var(--blue)' : 'var(--text3)' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {repeatType === 'custom' && (
                    <div style={{ display:'flex', gap:4, marginBottom:12, flexWrap:'wrap' }}>
                      {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d,i) => (
                        <button key={i} onClick={() => setRepeatDays(prev => prev.includes(i) ? prev.filter(x=>x!==i) : [...prev,i])}
                          style={{ width:36, height:36, borderRadius:'50%', border:'1.5px solid', cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:700,
                            background: repeatDays.includes(i) ? 'var(--blue)' : 'transparent',
                            borderColor: repeatDays.includes(i) ? 'var(--blue)' : 'var(--border)',
                            color: repeatDays.includes(i) ? 'white' : 'var(--text3)' }}>
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ marginBottom:12 }}>
                    <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', display:'block', marginBottom:4 }}>Repeat until</label>
                    <CalendarPicker value={repeatUntil} onChange={setRepeatUntil} compact />
                  </div>
                  <Btn variant="primary" size="sm" disabled={!repeatUntil || repeating} style={{ width:'100%' }}
                    onClick={async () => {
                      setRepeating(true);
                      try {
                        const res = await api.repeatEvent(event.id, { repeatType, repeatUntil, repeatDays: repeatType==='custom' ? repeatDays : undefined });
                        alert(`Created ${res.created} event${res.created!==1?'s':''}${res.skipped?' ('+res.skipped+' duplicates skipped)':''}`);
                        setShowRepeat(false);
                        onClose();
                      } catch(e) { alert('Error: ' + e.message); }
                      finally { setRepeating(false); }
                    }}>
                    {repeating ? 'Creating...' : 'Create Recurring Events'}
                  </Btn>
                </div>
              )}
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            {event && onDelete && (
              <Btn variant="danger" size="lg" onClick={async () => { if (await confirmAction('Delete this event?', { title: 'Delete Event', confirmLabel: 'Delete' })) onDelete(event); }}
                style={{ flexShrink:0 }}>
                <i className="fa-solid fa-trash" style={{ fontSize:12 }}></i>
              </Btn>
            )}
            <Btn variant="primary" size="lg" onClick={handleSave} disabled={!title.trim()} style={{ flex:1 }}>
              {event ? 'Save Changes' : 'Add Event'}
            </Btn>
            {onSaveAndSync && (
              <Btn variant="primary" size="lg" onClick={() => { if (!title.trim()) return; onSaveAndSync({ title, person: selectedPeople.length ? selectedPeople.join(",") : null, date, timeStart:tStart||null, timeEnd:tEnd||null, location:loc||null, notes:notes||null, url:url||null, files, contacts, travelType: travelType || null }, selCalendar); }} disabled={!title.trim()}
                style={{ flex:1, background:'rgba(52,168,83,0.15)', borderColor:'rgba(52,168,83,0.4)', color:'#34d399' }}>
                {event ? 'Save + Sync' : 'Add + GCal'}
              </Btn>
            )}
          </div>
        </div>
      </div>
      {ConfirmDialog}
    </div>
  );
}

function DropZone({ onFiles }) {
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pending, setPending] = useState([]);
  const [pdfData, setPdfData] = useState([]);
  const [error, setError] = useState('');
  const fileRef = useRef();
  const inp = { background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 10px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  const handleFiles = async (files) => {
    const pdfs = Array.from(files).filter(f => f.type==='application/pdf' || f.name.endsWith('.eml') || f.name.endsWith('.pdf') || f.type.startsWith('image/') || f.name.endsWith('.txt'));
    if (!pdfs.length) { setError('Drop PDFs, images, or email files'); return; }
    setProcessing(true); setError(''); setPending([]);
    try {
      const events = [];
      const pdfDataFiles = [];
      for (const f of pdfs) {
        const extracted = await extractEventsFromFile(f);
        console.log('[DropZone] Extracted from', f.name, ':', extracted);
        events.push(...extracted);
        await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = () => { pdfDataFiles.push({ name: f.name, type: f.type, data: reader.result }); resolve(); };
          reader.readAsDataURL(f);
        });
      }
      if (events.length === 0) {
        setError('No events found in the document. Try a different file.');
      } else {
        setPending(events.map((e, i) => ({ ...e, _key: Date.now() + i })));
        setPdfData(pdfDataFiles);
      }
    } catch(e) {
      console.error('[DropZone] Extraction error:', e);
      setError(`Extraction failed: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const updatePending = (key, field, value) => {
    setPending(prev => prev.map(e => e._key === key ? { ...e, [field]: value } : e));
  };

  const removePending = (key) => {
    setPending(prev => prev.filter(e => e._key !== key));
  };

  const confirmAll = () => {
    const fileAttachments = pdfData.map(f => ({ name: f.name, type: f.type, data: f.data }));
    const cleaned = pending.map(({ _key, ...rest }) => ({
      ...rest,
      files: [...(rest.files || []), ...fileAttachments],
    }));
    onFiles(cleaned, pdfData);
    setPending([]); setPdfData([]);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <SLabel>Drop Files to Extract Events</SLabel>
      {pending.length === 0 && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? 'var(--blue)' : 'var(--border)'}`,
            borderRadius: 14, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: dragging ? 'rgba(79,156,249,0.05)' : 'var(--bg3)',
            transition: 'all 0.15s',
          }}>
          <input ref={fileRef} data-dropzone-input type="file" multiple accept=".pdf,.eml,.txt,.jpg,.jpeg,.png,.heic,.webp" style={{ display:'none' }} onChange={e=>handleFiles(e.target.files)} />
          {processing ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginBottom:12 }}>
                <div style={{ width:28, height:28, border:'3px solid var(--border)', borderTop:'3px solid var(--blue)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:28 }}></i>
              </div>
              <p style={{ color:'var(--blue)', fontWeight:700, fontSize:15 }}>Extracting events...</p>
              <p style={{ color:'var(--text3)', fontSize:13, marginTop:4 }}>Claude AI is reading your document. This can take 15-30 seconds for complex schedules.</p>
              <div style={{ display:'flex', justifyContent:'center', gap:4, marginTop:10 }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:'var(--blue)', animation:`pulse 1.4s ease-in-out ${i*0.2}s infinite` }} />
                ))}
              </div>
              <style>{`@keyframes pulse { 0%,100% { opacity:0.3; transform:scale(0.8); } 50% { opacity:1; transform:scale(1.2); } }`}</style>
            </div>
          ) : (
            <div>
              <i className="fa-solid fa-paperclip" style={{ fontSize:32, display:'block', marginBottom:8 }}></i>
              <p style={{ color: 'var(--text)', fontWeight: 700 }}>Drop files to extract events</p>
              <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>PDFs, screenshots, photos, or emails — schedules, call sheets, flight confirmations</p>
            </div>
          )}
        </div>
      )}
      {error && <p style={{ color:'var(--red)', fontSize:13, marginTop:8 }}>{error}</p>}

      {/* Review extracted events before saving */}
      {pending.length > 0 && (
        <div style={{ border:'1px solid rgba(79,156,249,0.3)', borderRadius:14, background:'var(--bg3)' }}>
          <div style={{ padding:'12px 16px', background:'rgba(79,156,249,0.08)', borderBottom:'1px solid rgba(79,156,249,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <p style={{ fontSize:13, fontWeight:700, color:'var(--blue)', margin:0 }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight:6 }}></i>
              {pending.length} event{pending.length !== 1 ? 's' : ''} extracted — review before adding
            </p>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setPending([]); setPdfData([]); }}
                style={{ fontSize:12, fontWeight:700, color:'var(--text3)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>
                Discard
              </button>
              <button onClick={confirmAll}
                style={{ fontSize:12, fontWeight:700, color:'white', background:'var(--blue)', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontFamily:'inherit' }}>
                Add {pending.length} Event{pending.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
          <div style={{ padding:'10px 12px', display:'flex', flexDirection:'column', gap:8, overflow:'visible' }}>
            {pending.map(e => (
              <div key={e._key} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', position:'relative', overflow:'visible' }}>
                <button onClick={() => removePending(e._key)}
                  style={{ position:'absolute', top:8, right:10, background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:14, padding:0, lineHeight:1 }}>×</button>
                <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input value={e.title || ''} onChange={ev => updatePending(e._key, 'title', ev.target.value)}
                    placeholder="Event title" style={{ ...inp, fontWeight:700 }} />
                </div>
                <div style={{ marginBottom:6 }}>
                  <CalendarPicker value={e.date || ''} onChange={val => updatePending(e._key, 'date', val)} compact />
                </div>
                <div style={{ display:'flex', gap:8, marginBottom:6 }}>
                  <div style={{ flex:1 }}><TimePicker value={e.timeStart || ''} onChange={val => updatePending(e._key, 'timeStart', val)} placeholder="Start" /></div>
                  <div style={{ flex:1 }}><TimePicker value={e.timeEnd || ''} onChange={val => updatePending(e._key, 'timeEnd', val)} placeholder="End" /></div>
                </div>
                <LocationSearch value={e.location || ''} onChange={val => updatePending(e._key, 'location', val)} />
                <input value={e.notes || ''} onChange={ev => updatePending(e._key, 'notes', ev.target.value)}
                  placeholder="Notes" style={inp} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Location Search
function LocationSearch({ value, onChange }) {
  const [query, setQuery]     = React.useState(value || '');
  const [results, setResults] = React.useState([]);
  const [show, setShow]       = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [dropPos, setDropPos] = React.useState({ top:0, left:0, width:0 });
  const debounceRef           = React.useRef(null);
  const inputRef              = React.useRef(null);

  const fetchPlaces = async (text) => {
    if (text.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const token   = localStorage.getItem('ds_token');
      const res     = await fetch(`${API_URL}/places/autocomplete?input=${encodeURIComponent(text)}`, {
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
      });
      const data = await res.json();
      setResults(data.predictions || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    // Manual typing always clears any previously-captured placeId — the
    // address text no longer points at the same Google Places entity.
    // Callers that don't care about placeId can ignore the second arg.
    onChange(val, null);
    setShow(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPlaces(val), 300);
  };

  const select = (prediction) => {
    const desc = prediction.description || prediction;
    setQuery(desc);
    // Forward placeId alongside the address text when the caller wants
    // it (signature: onChange(value, placeId)). Older callers ignoring
    // the second arg keep working — they only see the new string.
    onChange(desc, prediction.placeId || null);
    setShow(false);
    setResults([]);
  };

  return (
    <div style={{ position: "relative", zIndex: 50 }}>
      <input
        ref={inputRef}
        value={query}
        onChange={handleChange}
        onFocus={() => setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 300)}
        placeholder="Search any venue, city, or address..."
        style={{ width:"100%", background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:10, padding:"12px 14px", fontSize:14, color:"var(--text)", outline:"none", boxSizing:"border-box" }}
      />
      {show && results.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, marginTop:4, background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.4)", zIndex:9999 }}>
          {results.map((r, i) => (
            <div key={i} onMouseDown={() => select(r)}
              style={{ padding:"11px 14px", cursor:"pointer", fontSize:14, color:"var(--text)", borderBottom: i < results.length-1 ? "1px solid var(--border)" : "none", display:"flex", alignItems:"center", gap:8 }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:12 }}></i>
              {r.description}
            </div>
          ))}
        </div>
      )}
      {loading && <div style={{ position:"absolute", right:12, top:14 }}><div style={{ width:14, height:14, border:"2px solid var(--border)", borderTop:"2px solid var(--blue)", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} /></div>}
    </div>
  );
}


function FlightInfoModal({ events, pdfFiles, onConfirm, onClose }) {
  const { people: artistList } = usePeople();
  const flights = events.filter(e => e.type === 'flight_departure' || e.type === 'flight_arrival');
  const flightKeys = [...new Set(flights.map(e => e.flightNumber).filter(Boolean))];
  const [info, setInfo] = useState({});

  // Extract unique passengers from events
  const passengers = [...new Set(flights.map(e => e.passengerInitials).filter(Boolean))];
  const PALETTE = ['#f87171','#fb923c','#fbbf24','#34d399','#06B6D4','#818cf8','#e879f9','#f472b6','#a78bfa','#38bdf8'];
  const [colorMap2, setColorMap2] = useState(Object.fromEntries(passengers.map((p,i) => [p, PALETTE[i % PALETTE.length]])));
  // Map passenger initials to person name
  const [passengerMap, setPassengerMap] = useState(
    Object.fromEntries(passengers.map(p => [p, '']))
  );

  const updateInfo = (key, field, value) => {
    setInfo(prev => ({ ...prev, [key]: { ...(prev[key]||{}), [field]: value } }));
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'var(--bg2)', borderRadius:16, padding:28, width:'100%', maxWidth:480, maxHeight:'80vh', overflowY:'auto', border:'1px solid var(--border)' }}>
        <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:6 }}><i className="fa-solid fa-plane" style={{ marginRight:6 }}></i> Flight Details</h3>
        <p style={{ fontSize:13, color:'var(--text3)', marginBottom:24 }}>Claude extracted {flights.length} flight event{flights.length!==1?'s':''}. Add driver and greeter info:</p>

        {flightKeys.length > 0 ? flightKeys.map(key => {
          const dep = flights.find(e => e.type==='flight_departure' && e.flightNumber===key);
          const arr = flights.find(e => e.type==='flight_arrival' && e.flightNumber===key);
          return (
            <div key={key} style={{ marginBottom:20, background:'var(--card)', borderRadius:12, padding:16, border:'1px solid var(--border)' }}>
              <p style={{ fontSize:14, fontWeight:700, color:'var(--blue)', marginBottom:12 }}>
                <i className="fa-solid fa-plane" style={{ marginRight:6 }}></i>{dep?.departureAirport||'?'} → {arr?.arrivalAirport||'?'} · {key}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>DEPARTURE GREETER</label>
                  <input value={info[key]?.greeter||''} onChange={e=>updateInfo(key,'greeter',e.target.value)}
                    placeholder="Name + phone number"
                    style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>ARRIVAL DRIVER</label>
                  <input value={info[key]?.driver||''} onChange={e=>updateInfo(key,'driver',e.target.value)}
                    placeholder="Name + phone number"
                    style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>ARRIVAL GREETER</label>
                  <input value={info[key]?.arrivalGreeter||''} onChange={e=>updateInfo(key,'arrivalGreeter',e.target.value)}
                    placeholder="Name + phone number"
                    style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
                </div>
              </div>
            </div>
          );
        }) : (
          <div style={{ marginBottom:20, background:'var(--card)', borderRadius:12, padding:16, border:'1px solid var(--border)' }}>
            <p style={{ fontSize:13, color:'var(--text2)', marginBottom:10 }}>Flight info</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>DEPARTURE GREETER</label>
                <input value={info['default']?.greeter||''} onChange={e=>updateInfo('default','greeter',e.target.value)}
                  placeholder="Name + phone number"
                  style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>ARRIVAL DRIVER</label>
                <input value={info['default']?.driver||''} onChange={e=>updateInfo('default','driver',e.target.value)}
                  placeholder="Name + phone number"
                  style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:'0.5px', display:'block', marginBottom:4 }}>ARRIVAL GREETER</label>
                <input value={info['default']?.arrivalGreeter||''} onChange={e=>updateInfo('default','arrivalGreeter',e.target.value)}
                  placeholder="Name + phone number"
                  style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
              </div>
            </div>
          </div>
        )}

        {passengers.length > 0 && (
          <div style={{ marginBottom:20 }}>
            <p style={{ fontSize:11, fontWeight:700, letterSpacing:'0.8px', color:'var(--text3)', marginBottom:10 }}>PASSENGERS</p>
            {passengers.map(initials => (
              <div key={initials} style={{ marginBottom:10 }}>
                <p style={{ fontSize:12, color:'var(--text3)', marginBottom:4 }}>"{initials}" on the manifest</p>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
                  {artistList.map(a => (
                    <button key={a.id} onClick={() => setPassengerMap(prev => ({...prev, [initials]: passengerMap[initials]===a.name?'':a.name}))}
                      style={{ padding:'5px 10px', borderRadius:8, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600,
                        background:passengerMap[initials]===a.name?'rgba(79,156,249,0.15)':'var(--bg3)',
                        borderColor:passengerMap[initials]===a.name?'var(--blue)':'var(--border)',
                        color:passengerMap[initials]===a.name?'var(--blue)':'var(--text2)' }}>
                      {a.name}
                    </button>
                  ))}
                  <button onClick={() => setPassengerMap(prev => ({...prev, [initials]: ''}))}
                    style={{ padding:'5px 10px', borderRadius:8, border:'1px solid', cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:600,
                      background:passengerMap[initials]===''?'rgba(79,156,249,0.15)':'var(--bg3)',
                      borderColor:passengerMap[initials]===''?'var(--blue)':'var(--border)',
                      color:passengerMap[initials]===''?'var(--blue)':'var(--text2)' }}>
                    No badge
                  </button>
                </div>
                <input value={passengerMap[initials]} onChange={e => setPassengerMap(prev => ({...prev, [initials]: e.target.value}))}
                  placeholder="Or type a name..."
                  style={{ width:'100%', background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 10px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box' }} />
              </div>
            ))}
          </div>
        )}
        <p style={{ fontSize:12, color:'var(--text3)', marginBottom:16 }}>
          <i className="fa-solid fa-paperclip" style={{ marginRight:4 }}></i>{pdfFiles.length} PDF{pdfFiles.length!==1?'s':''} will be attached to all flight events
        </p>
        <div style={{ display:'flex', gap:10 }}>
          <Btn variant="ghost" onClick={onClose} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" onClick={() => onConfirm(info, passengerMap)} style={{ flex:2 }}>Add {flights.length} Events →</Btn>
        </div>
      </div>
    </div>
  );
}



export { EventModal, DropZone, CalendarPicker, TimePicker, LocationSearch, resolveInitials };

export default function SheetBuilder() {
  const { team: TEAM, colorMap: PERSON_COLORS } = usePeople();
  const navigate  = useNavigate();
  const isMobile = useIsMobile();
  const { id }    = useParams();
  const [searchParams] = useSearchParams();
  const isEdit    = !!id;
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const [artists,  setArtists]  = useState([]);
  const [selArtist,setSelArtist]= useState(searchParams.get('artist') || '');
  const [selArtistIds, setSelArtistIds] = useState(searchParams.get('artist') ? [searchParams.get('artist')] : []);
  const [date,     setDate]     = useState(searchParams.get('date') || todayStr());
  const [city,     setCity]     = useState('');
  const [notes,    setNotes]    = useState('');
  const [emailTime,setEmailTime]= useState('19:00');
  const [emailSendDate,setEmailSendDate]= useState('');
  const artistObj = artists.find(a => a.id === selArtist);
  const [events,   setEvents]   = useState([]);
  const [weather,  setWeather]  = useState(null);
  const [gcal,     setGcal]     = useState([]);
  const [gcalLoading,setGcalLoading]= useState(false);
  const [gcalConnected, setGcalConnected] = useState(true);
  const [library,     setLibrary]     = useState([]);
  const [travelRecords, setTravelRecords] = useState([]);
  const [sheetTravelIds, setSheetTravelIds] = useState([]);
  const [showInlineTravel, setShowInlineTravel] = useState(false);
  const [editTravelRecord, setEditTravelRecord] = useState(null);
  const [showImportHelp, setShowImportHelp] = useState(false);
  const [viewerAccessGroups, setViewerAccessGroups] = useState([]);
  const [selectedEmailGroups, setSelectedEmailGroups] = useState([]);
  const [publishExcluded, setPublishExcluded] = useState(new Set());
  const [publishExpandedGroup, setPublishExpandedGroup] = useState(null);
  const [publishExtraEmail, setPublishExtraEmail] = useState('');
  const [publishExtraEmails, setPublishExtraEmails] = useState([]);
  const [publishAppUsers, setPublishAppUsers] = useState([]);
  const [publishSendPush, setPublishSendPush] = useState(true);
  const [sourceTab, setSourceTab] = useState('gcal');
  const [gcalTravelParsed, setGcalTravelParsed] = useState([]);
  const [gcalTravelLoading, setGcalTravelLoading] = useState(false);
  const [gcalTravelImporting, setGcalTravelImporting] = useState(null);
  const [libSearch,setLibSearch]= useState('');
  const [libTab,   setLibTab]   = useState('recent');
  const [showEvent,setShowEvent]= useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewSheet, setReviewSheet] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [flightInfoModal, setFlightInfoModal] = useState(null); // { events, pdfFile }
  const [editEvt,  setEditEvt]  = useState(null);
  const [extractedQueue, setExtractedQueue] = useState([]);
  const [extractedTravelQueue, setExtractedTravelQueue] = useState([]);
  const [extractedTravelIdx, setExtractedTravelIdx] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importedEvents, setImportedEvents] = useState([]);
  const [importedPdfData, setImportedPdfData] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [tab,      setTab]      = useState('build'); // build | preview

  const artistParam = searchParams.get('artist');

  useEffect(() => {
    api.getArtists().then(d => {
      setArtists(d.artists||[]);
      if (!d.artists?.length) return;
      if (artistParam && d.artists.find(a => a.id === artistParam)) {
        setSelArtist(artistParam);
        if (!selArtistIds.length) setSelArtistIds([artistParam]);
      } else if (!selArtist) {
        setSelArtist(d.artists[0].id);
        if (!selArtistIds.length) setSelArtistIds([d.artists[0].id]);
      }
    });
  }, [artistParam]);

  useEffect(() => {
    apiFetch('/users').then(users => {
      const distContacts = artistObj?.distributionContacts || [];
      setPublishAppUsers((users || []).filter(u => u.email).map(u => {
        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
        const contactMatch = distContacts.find(c => c.email?.toLowerCase() === u.email.toLowerCase());
        return {
          email: u.email,
          name: fullName || contactMatch?.name || u.name || null,
          photo: u.photo || null,
        };
      }));
    }).catch(() => {});
  }, [artistObj]);

  // Also sync when param changes after mount
  useEffect(() => {
    if (artistParam && artists.length && artists.find(a => a.id === artistParam)) {
      setSelArtist(artistParam);
    }
  }, [artistParam, artists]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        const sheetDetail = await apiFetch('/sheets/' + id);
        setSelArtist(sheetDetail.artistId);
        setSelArtistIds(sheetDetail.artistIds || [sheetDetail.artistId]);
        setDate(sheetDetail.date);
        setCity(sheetDetail.city || '');
        setNotes(sheetDetail.notes || '');
        setEmailTime(sheetDetail.emailScheduledAt || '19:00');
        setEmailSendDate(sheetDetail.emailSendDate || '');
        setSheetTravelIds(sheetDetail.travelIds || []);
        const evts = sheetDetail.events || [];
        setEvents(evts.map(e => ({...e, isNew:false})));
      } catch(e) { console.error(e); }
    };
    load();
  }, [id]);

  // Track dirty state — mark dirty when user makes changes
  useEffect(() => {
    if (events.length > 0 || notes || city) setIsDirty(true);
  }, [events, notes, city]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Auto-save as draft every 30 seconds if dirty
  useEffect(() => {
    if (!isDirty || !selArtist || saving) return;
    const timer = setTimeout(async () => {
      try {
        const sheetId = await ensureSaved();
        if (sheetId) {
          setIsDirty(false);
          setLastSaved(new Date());
        }
      } catch (e) { /* silent fail for auto-save */ }
    }, 30000);
    return () => clearTimeout(timer);
  }, [isDirty, events, notes, city, selArtist]);

  useEffect(() => {
    if (!selArtist || !date) return;
    setGcalLoading(true);
    api.getCalEvents(selArtist, date).then(d => { setGcal(d.events||[]); setGcalConnected(true); }).catch((e) => { setGcal([]); if (e?.message?.includes('No Google Calendar')) setGcalConnected(false); }).finally(()=>setGcalLoading(false));
    // Detect travel from GCal (scan only, user clicks + Add)
    setGcalTravelLoading(true);
    api.gcalTravel(selArtist, date).then(d => setGcalTravelParsed(d.parsed||[])).catch(()=>setGcalTravelParsed([])).finally(()=>setGcalTravelLoading(false));
  }, [selArtist, date]);

  useEffect(() => {
    if (!selArtist) return;
    api.getEventLibrary(selArtist).then(d => setLibrary(d||[])).catch(()=>{});
  }, [selArtist]);

  useEffect(() => {
    if (!selArtist || !date) return;
    api.getTravel(selArtist, date).then(d => setTravelRecords(Array.isArray(d)?d:[])).catch(()=>{});
  }, [selArtist, date]);

  // Sync local events with travel record changes (titles, notes update after travel edit)
  const syncTravelEvents = (result) => {
    if (result?.events?.length) {
      setEvents(prev => {
        const updated = [...prev];
        for (const evt of result.events) {
          const idx = updated.findIndex(e => e.id === evt.id);
          if (idx >= 0) updated[idx] = { ...updated[idx], ...evt };
        }
        return updated;
      });
    }
    // Also update travel record in local state
    setTravelRecords(prev => {
      const idx = prev.findIndex(r => r.id === result.id);
      if (idx >= 0) { const u = [...prev]; u[idx] = result; return u; }
      return [...prev, result];
    });
  };

  useEffect(() => {
    if (!city || !date) { setWeather(null); return; }
    apiFetch(`/sheets/weather?location=${encodeURIComponent(city)}&date=${date}`).then(w => setWeather(w?.tempHigh ? w : null)).catch(() => setWeather(null));
  }, [city, date]);

  // Hide mobile bottom tab bar while the Preview & Publish overlay is open
  useEffect(() => {
    if (showPreview) document.body.setAttribute('data-modal-open', 'true');
    else document.body.removeAttribute('data-modal-open');
    return () => document.body.removeAttribute('data-modal-open');
  }, [showPreview]);

  // User-initiated date change: reset sheet context so today's events don't bleed into another day.
  // Only runs from the CalendarPicker click — the initial sheet load calls setDate directly.
  const handleDateChange = (newDate) => {
    if (!newDate || newDate === date) return;
    setDate(newDate);
    setEvents([]);
    setCity('');
    setNotes('');
    setWeather(null);
    setSheetTravelIds([]);
    setTravelRecords([]);
    setSelectedEmailGroups([]);
    setPublishExcluded(new Set());
    setPublishExtraEmails([]);
  };

  // Smart import — detects travel docs vs schedules
  const isTravelDoc = (fileName) => {
    const lower = (fileName || '').toLowerCase();
    return /flight|itinerary|booking|confirmation|ticket|reservation|hotel|travel|prive|netjets|charter/.test(lower);
  };

  const TRAVEL_EXTRACT_PROMPT = `Extract ALL travel reservations from this document. Return ONLY a valid JSON array. Do NOT use newlines inside string values.
Each item must have: type ("flight", "hotel", "car", "ground", or "train"), startDate (YYYY-MM-DD), endDate (YYYY-MM-DD).
For FLIGHTS: flightType ("commercial" or "private" — use "private" for NetJets, Privé Jets, charter, any tail numbers), airline, flightNumber, departureAirport (3-4 letter code), arrivalAirport, departureCity (city and state/country e.g. "New York City, NY"), arrivalCity, flightDepartureTime (HH:MM 24hr), flightArrivalTime, flightDuration (e.g. "10hr 45min"), cabinClass (e.g. "First", "Business", "Economy"), seat, flightConfirmation, passenger (comma-separated ALL names in NATURAL order "First Last" — convert airline format LAST/FIRST to "First Last", e.g. "WEIR/GEORGE" → "George Weir", "JONAS II/PAUL KEVIN" → "Kevin Jonas II").
For PRIVATE also: tailNumber, aircraftType, operator, departureFBO, departureFBOPhone, departureFBOAddress, arrivalFBO, arrivalFBOPhone, arrivalFBOAddress, requestedBy, requestedByPhone.
For HOTELS: hotelName, hotelAddress, hotelConfirmation, roomType.
For CARS: carCompany, carPickupLocation, carDropoffLocation, carConfirmation.
For GROUND: driverName, driverPhone, pickupLocation, dropoffLocation.
For TRAINS: trainCompany, trainFrom, trainTo, trainConfirmation, departureTime, arrivalTime.
Do NOT include a notes field — leave notes null. Return [] if nothing found.`;

  const handleImportFiles = async (files, onProgress) => {
    const allEvents = [];
    const allTravel = [];
    const pdfDataFiles = [];

    // Simulate smooth progress during API calls
    let progressTarget = 0;
    let progressCurrent = 0;
    const progressInterval = setInterval(() => {
      if (progressCurrent < progressTarget - 2) {
        progressCurrent += Math.random() * 3 + 0.5;
        if (progressCurrent > progressTarget - 2) progressCurrent = progressTarget - 2;
        if (onProgress) onProgress(Math.round(progressCurrent));
      }
    }, 200);

    try {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      progressTarget = 5;
      if (onProgress) onProgress(5);

      const reader = new FileReader();
      const dataUrl = await new Promise(r => { reader.onload = () => r(reader.result); reader.readAsDataURL(file); });
      const base64 = dataUrl.split(',')[1];
      progressTarget = 15;
      const mimeType = file.type || 'application/pdf';

      pdfDataFiles.push({ name: file.name, type: file.type, data: dataUrl });

      // Detect if this is a travel doc by filename
      const isTravelFile = isTravelDoc(file.name);
      progressTarget = 85;

      if (isTravelFile) {
        // Travel-specific extraction
        try {
          const res = await apiFetch('/sheets/extract-events', { method: 'POST', body: JSON.stringify({ base64, mimeType, prompt: TRAVEL_EXTRACT_PROMPT }) });
          let clean = (res.text || '').replace(/```json|```/g, '').trim();
          const arrMatch = clean.match(/\[[\s\S]*\]/);
          if (arrMatch) {
            clean = arrMatch[0].replace(/,\s*]/g, ']').replace(/,\s*}/g, '}');
            const extracted = JSON.parse(clean);
            allTravel.push(...extracted);
          }
        } catch {}
      } else {
        // Event extraction (schedules, press docs, itineraries)
        try {
          const { extractEventsFromFile } = await import('../lib/gemini');
          const events = await extractEventsFromFile(file);
          allEvents.push(...events);
        } catch {}
      }

      progressTarget = 95;
      progressCurrent = 90;
      if (onProgress) onProgress(90);
    }

    progressTarget = 100;
    progressCurrent = 100;
    if (onProgress) onProgress(100);

    // Merge duplicate flights across all uploaded files
    // Matches on: flight number + airports, OR airports + times + date (for private/no flight number)
    if (allTravel.length > 1) {
      const merged = [];
      const seen = {};
      for (const t of allTravel) {
        if (t.type === 'flight' && t.departureAirport && t.arrivalAirport) {
          // Build key — with or without flight number
          const key = t.flightNumber
            ? `${t.flightNumber}-${t.departureAirport}-${t.arrivalAirport}`
            : `${t.departureAirport}-${t.arrivalAirport}-${t.flightDepartureTime||''}-${t.startDate||''}`;
          if (seen[key] !== undefined) {
            const existing = merged[seen[key]];
            // Merge passengers
            const ep = (existing.passenger || '').split(',').map(p => p.trim()).filter(Boolean);
            const np = (t.passenger || '').split(',').map(p => p.trim()).filter(Boolean);
            existing.passenger = [...new Set([...ep, ...np])].join(', ');
            // Merge seats
            if (t.seat && existing.seat && !existing.seat.includes(t.seat)) existing.seat = `${existing.seat}, ${t.seat}`;
            else if (t.seat && !existing.seat) existing.seat = t.seat;
            // Merge confirmation codes
            if (t.flightConfirmation && existing.flightConfirmation && !existing.flightConfirmation.includes(t.flightConfirmation)) {
              existing.flightConfirmation = `${existing.flightConfirmation}, ${t.flightConfirmation}`;
            } else if (t.flightConfirmation && !existing.flightConfirmation) existing.flightConfirmation = t.flightConfirmation;
            // Fill in any missing fields from the new record
            ['departureCity','arrivalCity','flightDuration','cabinClass','airline','flightNumber','tailNumber','aircraftType','operator'].forEach(f => {
              if (t[f] && !existing[f]) existing[f] = t[f];
            });
          } else {
            seen[key] = merged.length;
            merged.push({ ...t });
          }
        } else {
          merged.push(t);
        }
      }
      allTravel.length = 0;
      allTravel.push(...merged);
    }

    // Show travel in TravelFormModal
    if (allTravel.length > 0) {
      setExtractedTravelQueue(allTravel);
      setExtractedTravelIdx(0);
    }

    // Show events in review panel
    if (allEvents.length > 0) {
      setImportedEvents(allEvents.map((e, i) => ({ ...e, _key: Date.now() + i })));
      setImportedPdfData(pdfDataFiles);
    }

    if (allEvents.length === 0 && allTravel.length === 0) {
      alert('No events or travel found in file');
    }
    } finally { clearInterval(progressInterval); }
  };

  const BAD_PERSONS = ['everyone', 'ev', 'jonas brothers', 'jb', 'family', 'all', 'group'];
  const addExtracted = (extracted, pdfFiles) => {
    if (!extracted || !extracted.length) { alert('No events found in the document'); return; }
    const cleaned = extracted.map(e => ({
      ...e,
      person: (e.person && BAD_PERSONS.includes(e.person.trim().toLowerCase())) ? null : e.person
    }));
    const newEvts = cleaned.map((e, i) => ({ ...e, id: `extracted-${Date.now()}-${i}`, isNew: true }));
    setEvents(prev => [...prev, ...newEvts]);
  };

  const confirmFlightInfo = (extracted, pdfFiles, flightInfoByKey, passengerMap={}) => {
    extracted = extracted.map(e => ({ ...e, person: (e.passengerInitials && passengerMap[e.passengerInitials]) ? passengerMap[e.passengerInitials] : null }));
    const newEvts = extracted.map((e, i) => {
      let notes = e.notes || '';
      if (e.type === 'flight_departure' || e.type === 'flight_arrival') {
        const key = e.flightNumber || i;
        const info = flightInfoByKey[key] || {};
        if (e.type === 'flight_departure') {
          if (info.greeter) notes += `\nGreeter: ${info.greeter}`;
        }
        if (e.type === 'flight_arrival') {
          if (info.driver) notes += `\nDriver: ${info.driver}`;
          if (info.arrivalGreeter) notes += `\nGreeter: ${info.arrivalGreeter}`;
        }
      }
      return {
        ...e,
        notes: notes || null,
        files: pdfFiles.map(f => ({ name: f.name, type: f.type, data: f.data })),
        id: `extracted-${Date.now()}-${i}`,
        isNew: true
      };
    });
    setEvents(prev => [...prev, ...newEvts]);
    setFlightInfoModal(null);
  };

  const stripHtml = (html) => {
    if (!html) return null;
    // Extract first URL before stripping
    return html
      .replace(/<a[^>]*href="([^"]*)"[^>]*>.*?<\/a>/gi, (_, href) => href) // replace links with just the URL
      .replace(/<br\s*\/?>/gi, '\n')  // br to newline
      .replace(/<[^>]+>/g, '')           // strip remaining tags
      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim() || null;
  };

  const addGcalEvent = (ge) => {
    // All-day events go to notes
    if (ge.isAllDay || !ge.timeStart) {
      const noteText = ge.title + (ge.notes ? ' — ' + stripHtml(ge.notes).slice(0, 100) : '');
      setNotes(prev => prev ? prev + ', ' + noteText : noteText);
      return;
    }

    const rawNotes = stripHtml(ge.notes);
    // Extract Zoom/Meet URL first
    const urlMatch = (rawNotes||'').match(/https?:\/\/(us0[0-9]web\.zoom\.us|zoom\.us|meet\.google\.com)[^\s"<>]*/i);
    const fallbackUrl = (rawNotes||'').match(/https?:\/\/[^\s"<>]+/);
    const url = urlMatch ? urlMatch[0].replace(/[.,;)>]+$/,'') : (fallbackUrl ? fallbackUrl[0].replace(/[.,;)>]+$/,'') : null);
    // Strip all boilerplate
    let cleanNotes = rawNotes || '';
    cleanNotes = cleanNotes
      .replace(/HANK is inviting[^.]*\./i, '')
      .replace(/[^.]*inviting you to a scheduled[^.]*\./i, '')
      .replace(/Join Zoom Meeting[\s\S]*/i, '')
      .replace(/Join this Zoom[\s\S]*/i, '')
      .replace(/Topic:[^\n]*/i, '')
      .replace(/Meeting ID:[\s\S]*/i, '')
      .replace(/Passcode:[\s\S]*/i, '')
      .replace(/One tap mobile[\s\S]*/i, '')
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    cleanNotes = cleanNotes || null;
    // Stamp GCal-imported events with the artist's tz short code so the
    // rendering layer (email + iOS + admin views) can convert when the
    // event lands on a foreign-tz sheet (e.g. an ET event on a BA sheet
    // shows as 4:00 PM with a "3:00 PM ET" indicator). On same-tz sheets
    // the tag matches and no conversion happens — invisible to the user.
    const sourceTz = shortCodeForIana(artistObj?.timezone) || artistObj?.timezone || null;
    const evt = { id:`gcal-${ge.gcalId||Date.now()}`, title:ge.title, person:ge.calendar||null, timeStart:ge.timeStart, timeEnd:ge.timeEnd, timezone:sourceTz, location:ge.location||null, notes:cleanNotes, url, isNew:true };
    setEvents(prev => [...prev, evt]);
  };

  const handleSaveEvent = (evt) => {
    if (editEvt && editEvt.id && !editEvt.id.toString().startsWith('extracted-') && events.some(e => e.id === editEvt.id)) {
      // Editing existing event
      setEvents(prev => prev.map(e => e.id===editEvt.id ? { ...e, ...evt } : e));
    } else {
      // New event (including extracted)
      setEvents(prev => [...prev, { ...evt, id:`new-${Date.now()}`, isNew:true }]);
    }

    // If editing an imported event, remove it from the review panel
    if (editEvt && editEvt._key) {
      setImportedEvents(prev => prev.filter(x => x._key !== editEvt._key));
    }

    // If there are more extracted events in queue, show the next one
    if (extractedQueue.length > 0) {
      const currentIdx = extractedQueue.indexOf(editEvt);
      const nextIdx = currentIdx + 1;
      if (nextIdx < extractedQueue.length) {
        setEditEvt(extractedQueue[nextIdx]);
        return; // keep modal open
      } else {
        setExtractedQueue([]);
      }
    }
    setShowEvent(false); setEditEvt(null);
  };

  const handleSaveAndSync = async (evt, calendarId) => {
    // Save event first
    let savedEvt;
    if (editEvt) {
      savedEvt = await api.updateEvent(editEvt.id, { ...evt, artistId: selArtist, date });
      setEvents(prev => prev.map(e => e.id===editEvt.id ? { ...e, ...savedEvt } : e));
    } else {
      savedEvt = await api.createEvent({ ...evt, artistId: selArtist, date, daysheetId: id || null });
      setEvents(prev => [...prev, savedEvt]);
      // Link to sheet if it exists
      if (id && savedEvt.id) await api.linkEvent(savedEvt.id, id).catch(() => {});
    }
    // Push to Google Calendar
    if (savedEvt?.id) {
      try { await api.pushToGCal(savedEvt.id, calendarId || undefined); } catch (e) { console.error('GCal push failed:', e); }
    }
    setShowEvent(false); setEditEvt(null);
  };

  const cleanPerson = (p) => (p && ['everyone','ev','jonas brothers','jb','family','all','group'].includes(p.trim().toLowerCase())) ? null : p;
  const sortByTime = (arr) => [...arr].sort((a,b) => { if (!a.timeStart && !b.timeStart) return 0; if (!a.timeStart) return 1; if (!b.timeStart) return -1; return a.timeStart.localeCompare(b.timeStart); });

  const saveAll = async (sheetId, publish) => {
    // Step 1: Handle events
    let finalEventIds;
    if (isEdit) {
      const sheetData = await apiFetch('/sheets/' + sheetId);
      const existingEvts = sheetData.events || [];
      const existingIds = new Set(existingEvts.map(e => e.id));

      const kept = events.filter(e => !e.isNew && existingIds.has(e.id));
      const added = events.filter(e => e.isNew);

      // Update kept events sequentially to avoid JSON file race conditions
      for (const e of kept) {
        await api.updateEvent(e.id, { title:e.title, person:cleanPerson(e.person), timeStart:e.timeStart, timeEnd:e.timeEnd, location:e.location, notes:e.notes, url:e.url, sortOrder:e.sortOrder }).catch(()=>{});
      }

      // Create new events
      let newIds = [];
      if (added.length) {
        const cleanAdded = sortByTime(added).map(e => ({ ...e, person: cleanPerson(e.person) }));
        const created = await api.bulkEvents({ artistId:selArtist, date, events: cleanAdded });
        newIds = (created || []).map(e => e.id);
      }

      finalEventIds = [...kept.map(e => e.id), ...newIds];
    } else {
      const cleanEvts = sortByTime(events).map(e => ({ ...e, person: cleanPerson(e.person) }));
      const created = await api.bulkEvents({ artistId:selArtist, date, events: cleanEvts });
      finalEventIds = (created || []).map(e => e.id);
    }

    // Step 2: Update sheet with EVERYTHING in one call
    await api.updateSheet(sheetId, {
      city: city || null,
      notes: notes || null,
      emailScheduledAt: emailTime,
      emailSendDate: emailSendDate || (() => { const d=new Date(date+'T12:00:00'); d.setDate(d.getDate()-1); return d.toISOString().slice(0,10); })(),
      eventIds: finalEventIds,
    });

    // Step 3: Publish if needed
    if (publish) {
      await apiFetch(`/sheets/${sheetId}/publish`, {
        method: 'POST',
        body: JSON.stringify({ emailGroupIds: selectedEmailGroups.length ? selectedEmailGroups : undefined, sendPush: publishSendPush }),
      });
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      let sheetId = id;
      if (!isEdit) {
        const _sendDate = emailSendDate || (() => { const d=new Date(date+'T12:00:00'); d.setDate(d.getDate()-1); return d.toISOString().slice(0,10); })();
        const artistIds = selArtistIds.length > 1 ? selArtistIds : undefined;
        const res = await api.createSheet({ artistId:selArtist, artistIds, date, city:city||null, notes:notes||null, emailScheduledAt:emailTime, emailSendDate:_sendDate, status:'draft' });
        sheetId = res?.id;
        if (!sheetId) throw new Error('No sheet ID: ' + JSON.stringify(res));
      }
      await saveAll(sheetId, true);
      navigate(`/admin/sheets/${sheetId}`);
    } catch(e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      let sheetId = id;
      if (!isEdit) {
        // Check for existing sheet first to avoid duplicates
        try {
          const existing = await api.getSheets(selArtist);
          const found = (existing.sheets || []).find(s => s.date === date);
          if (found) sheetId = found.id;
        } catch {}
        if (!sheetId) {
          const _sendDate = emailSendDate || (() => { const d=new Date(date+'T12:00:00'); d.setDate(d.getDate()-1); return d.toISOString().slice(0,10); })();
          const artistIds = selArtistIds.length > 1 ? selArtistIds : undefined;
          const res = await api.createSheet({ artistId:selArtist, artistIds, date, city:city||null, notes:notes||null, emailScheduledAt:emailTime, emailSendDate:_sendDate, status:'draft' });
          sheetId = res?.id;
          if (!sheetId) throw new Error('No sheet ID: ' + JSON.stringify(res));
        }
      }
      await saveAll(sheetId, false);
      setIsDirty(false);
      navigate(`/admin/sheets/${sheetId}/edit`, { replace: true });
    } catch(e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Save silently and return sheet ID (for PDF/email preview)
  const ensureSaved = async () => {
    let sheetId = id;
    if (!sheetId) {
      // Check if a sheet already exists for this artist + date before creating
      try {
        const existing = await api.getSheets(selArtist);
        const found = (existing.sheets || []).find(s => s.date === date);
        if (found) {
          sheetId = found.id;
        }
      } catch {}
      if (!sheetId) {
        const _sendDate = emailSendDate || (() => { const d=new Date(date+'T12:00:00'); d.setDate(d.getDate()-1); return d.toISOString().slice(0,10); })();
        const artistIds = selArtistIds.length > 1 ? selArtistIds : undefined;
        const res = await api.createSheet({ artistId:selArtist, artistIds, date, city:city||null, notes:notes||null, emailScheduledAt:emailTime, emailSendDate:_sendDate, status:'draft' });
        sheetId = res?.id;
      }
      // Update URL so future saves update instead of create
      if (sheetId) window.history.replaceState({}, '', `/admin/sheets/${sheetId}/edit`);
    }
    if (sheetId) await saveAll(sheetId, false);
    return sheetId;
  };

  const isPastMidnight = (t) => t && t < '03:00';
  const workDayKey = (e) => {
    if (e.nextDay) return '99:' + (e.timeStart || '99:99');
    if (!e.timeStart) return '98:99';
    if (isPastMidnight(e.timeStart)) return '24:' + e.timeStart;
    return e.timeStart;
  };
  const sortedEvents = [...events].sort((a,b) => workDayKey(a).localeCompare(workDayKey(b)));
  const isGcalAdded = (ge) => (ge.isAllDay || !ge.timeStart) ? notes.includes(ge.title) : events.some(e => e.title === ge.title);
  const removeGcalEvent = (ge) => { if (ge.isAllDay || !ge.timeStart) setNotes(p => p.split(',').map(s=>s.trim()).filter(s=>!s.includes(ge.title)).join(', ')); else setEvents(p => p.filter(e=>e.title!==ge.title)); };

  // Filter out GCal events that are detected as travel (shown in Travel tab instead)
  // Only filter once travel scan is done, so events don't flash then disappear
  const gcalTravelIds = new Set(gcalTravelParsed.map(p => p.gcalEventId).filter(Boolean));
  const gcalFiltered = gcalTravelLoading ? [] : gcal.filter(ge => !gcalTravelIds.has(ge.gcalEventId))
    .sort((a, b) => {
      const aAllDay = a.isAllDay || !a.timeStart;
      const bAllDay = b.isAllDay || !b.timeStart;
      if (aAllDay && !bAllDay) return -1;
      if (!aAllDay && bAllDay) return 1;
      return (a.timeStart || '').localeCompare(b.timeStart || '');
    });

  const accentColor = artistObj?.color || '#1E40AF';
  const dateDisplay = date ? new Date(date+'T12:00:00').toLocaleDateString('en-US',{weekday:'long', month:'long', day:'numeric', year:'numeric'}) : '';
  const dateShort = date ? new Date(date+'T12:00:00').toLocaleDateString('en-US',{month:'long', day:'numeric'}) : '';
  const travelIcons = { flight:'fa-plane', hotel:'fa-hotel', carservice:'fa-car-side', car:'fa-car', ground:'fa-van-shuttle', train:'fa-train' };
  const travelColors = { flight:'var(--blue)', hotel:'#06B6D4', carservice:'#06B6D4', car:'#fbbf24', ground:'#f472b6', train:'#a78bfa' };

  return (
    <div style={{ maxWidth:620, margin:'0 auto', minHeight:'100vh', background:'var(--bg)', padding: isMobile ? '0 0 80px' : 0 }}>

      {/* ── Sticky Top Bar (desktop only) ── */}
      {!isMobile && (
        <div style={{ position:'sticky', top:0, zIndex:150, background:'var(--bg2)', backdropFilter:'blur(20px)', borderBottom:'1px solid var(--border)', padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <button onClick={()=>navigate(-1)} style={{ background:'none', border:'none', color:'var(--text2)', fontSize:14, cursor:'pointer', fontFamily:'inherit', fontWeight:600, display:'flex', alignItems:'center', gap:6 }}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize:12 }}></i> Back
          </button>
          {lastSaved && <span style={{ fontSize:10, color:'var(--text3)', display:'flex', alignItems:'center', gap:4 }}><i className="fa-solid fa-check" style={{ fontSize:8, color:'#22c55e' }}></i>Draft saved</span>}
          <div />
        </div>
      )}

      <div style={{ padding:'20px 16px 0' }}>

        {/* ── Sheet Header (Live Preview) ── */}
        <div style={{ background:'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius:16, overflow:'hidden', marginBottom:20, border:'1px solid var(--border)' }}>
          <div style={{ height:4, background: selArtistIds.length > 1 ? `linear-gradient(to right, ${selArtistIds.map(id => artists.find(a=>a.id===id)?.color || accentColor).join(', ')})` : accentColor }} />
          <div style={{ padding:'20px 24px', textAlign:'center' }}>
            {selArtistIds.length > 1 ? (
              <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:8 }}>
                {selArtistIds.map(id => {
                  const a = artists.find(x => x.id === id);
                  if (!a?.photo) return null;
                  return (
                    <div key={id} style={{ width:44, height:44, borderRadius:'50%', overflow:'hidden', border:`2.5px solid ${a.color || accentColor}40`, flexShrink:0 }}>
                      <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    </div>
                  );
                })}
              </div>
            ) : artistObj?.photo ? (
              <div style={{ margin:'0 auto 8px', width:48, height:48, borderRadius:'50%', overflow:'hidden', border:`2px solid ${accentColor}40` }}>
                <img src={artistObj.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </div>
            ) : artistObj?.emoji ? (
              <div style={{ fontSize:32, marginBottom:6 }}>{artistObj.emoji}</div>
            ) : null}
            <p style={{ fontSize:20, fontWeight:900, color:'#fff', margin:0 }}>{selArtistIds.length > 1 ? selArtistIds.map(id => artists.find(a => a.id === id)?.name).filter(Boolean).join(' & ') : (artistObj?.name || 'Day Sheet')}</p>
            <p style={{ fontSize:13, color:'#94a3b8', marginTop:4 }}>{dateDisplay}</p>
            {city && <p style={{ fontSize:12, color:'#64748b', marginTop:2 }}><i className="fa-solid fa-location-dot" style={{ marginRight:4, fontSize:10 }}></i>{city}</p>}
            {/* Travel quick icons — only show for travel on the sheet */}
            {travelRecords.filter(r => events.some(e => e.travelId === r.id)).length > 0 && (
              <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12, flexWrap:'wrap' }}>
                {travelRecords.filter(r => events.some(e => e.travelId === r.id)).map(r => {
                  const color = travelColors[r.type] || 'var(--text3)';
                  const label = r.type==='flight' ? `${r.departureAirport||''} \u2192 ${r.arrivalAirport||''}`.trim()
                    : r.type==='hotel' ? r.hotelName || 'Hotel'
                    : r.type==='carservice' ? r.carServiceCompany || 'Car'
                    : r.hotelName||r.carCompany||r.driverName||r.type;
                  return (
                    <div key={r.id} style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:8, border:`1px solid ${color}30`, background:`${color}10`, fontSize:11, fontWeight:700, color }}>
                      <i className={`fa-solid ${travelIcons[r.type]||'fa-suitcase'}`} style={{ fontSize:10 }}></i>
                      {label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Artist Selector (circle icons — multi-select for joint sheets) ── */}
        <div style={{ display:'flex', gap:14, marginBottom:4, overflowX:'auto', paddingBottom:4 }}>
          {artists.map(a => {
            const selected = selArtistIds.includes(a.id);
            const isPrimary = a.id === selArtist;
            const color = a.color || '#1E40AF';
            return (
              <button key={a.id} onClick={() => {
                if (isEdit) {
                  // Editing: single select only
                  setSelArtist(a.id); setSelArtistIds([a.id]);
                } else {
                  // Creating: toggle multi-select
                  if (selected && selArtistIds.length > 1) {
                    const newIds = selArtistIds.filter(id => id !== a.id);
                    setSelArtistIds(newIds);
                    if (isPrimary) setSelArtist(newIds[0]);
                  } else if (!selected) {
                    setSelArtistIds(prev => [...prev, a.id]);
                    if (!selArtist) setSelArtist(a.id);
                  }
                }
              }} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', padding:0, minWidth:68, flexShrink:0 }}>
                <div style={{ position:'relative', width:60, height:60 }}>
                  <div style={{ width:60, height:60, borderRadius:'50%', overflow:'hidden', background:`${color}20`, border: selected ? `2.5px solid ${color}` : '2.5px solid transparent', transition:'all 0.15s', opacity: selected ? 1 : 0.4 }}>
                    {a.photo ? (
                      <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    ) : (
                      <div style={{ width:'100%', height:'100%', background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, fontWeight:800, color:'#fff' }}>
                        {a.emoji || a.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  {selected && <div style={{ position:'absolute', bottom:-2, right:-2, width:20, height:20, borderRadius:'50%', background:'#22c55e', border:'2.5px solid var(--bg2)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1 }}><i className="fa-solid fa-check" style={{ fontSize:9, color:'#fff' }}></i></div>}
                </div>
                <span style={{ fontSize:11, fontWeight: selected ? 700 : 500, color: selected ? 'var(--text)' : 'var(--text3)', whiteSpace:'nowrap', maxWidth:68, overflow:'hidden', textOverflow:'ellipsis' }}>{a.name?.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
        {/* Joint sheet label */}
        {selArtistIds.length > 1 && (
          <div style={{ marginBottom:12, padding:'8px 14px', borderRadius:10, background:'rgba(30,64,175,0.06)', border:'1px solid rgba(30,64,175,0.15)', display:'flex', alignItems:'center', gap:8 }}>
            <i className="fa-solid fa-users" style={{ fontSize:12, color:'var(--blue)' }}></i>
            <span style={{ fontSize:13, fontWeight:700, color:'var(--blue)' }}>Joint Day Sheet</span>
            <span style={{ fontSize:12, color:'var(--text2)' }}>— {selArtistIds.map(id => artists.find(a => a.id === id)?.name?.split(' ')[0]).filter(Boolean).join(' & ')}</span>
          </div>
        )}

        {/* ── Date + Starting Location ── */}
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:10, marginBottom:16, position:'relative', zIndex:60 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6 }}>DATE</label>
            <CalendarPicker value={date} onChange={handleDateChange} />
          </div>
          <div style={{ position:'relative', zIndex:60 }}>
            <label style={{ fontSize:11, fontWeight:700, color:'var(--text3)', letterSpacing:0.5, display:'block', marginBottom:6 }}>STARTING LOCATION</label>
            <LocationSearch value={city} onChange={setCity} />
          </div>
        </div>

        {/* ── Action Buttons: + Custom Event  |  Import ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
          <button onClick={()=>{ setEditEvt(null); setShowEvent(true); }} style={{ padding:'12px 16px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'1.5px solid transparent', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.25)' }}>
            <i className="fa-solid fa-plus" style={{ fontSize:11 }}></i> Create Event
          </button>
          <div style={{ position:'relative', display:'flex' }}>
            <input type="file" id="import-file-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.eml,.txt,.doc,.docx,image/*,application/pdf" multiple style={{ display:'none' }}
              onChange={async e => { if (e.target.files?.length) { setImporting(true); setImportProgress(0); try { await handleImportFiles(Array.from(e.target.files), setImportProgress); } catch(err) { alert('Extraction failed: ' + err.message); } finally { setImporting(false); setImportProgress(0); } e.target.value = ''; }}} />
            <button
              onClick={()=> document.getElementById('import-file-input').click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='rgba(168,85,247,0.8)'; e.currentTarget.style.background='rgba(168,85,247,0.08)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor='rgba(168,85,247,0.4)'; e.currentTarget.style.background='transparent'; }}
              onDrop={async e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor='rgba(168,85,247,0.4)'; e.currentTarget.style.background='transparent'; if (e.dataTransfer.files?.length) { setImporting(true); setImportProgress(0); try { await handleImportFiles(Array.from(e.dataTransfer.files), setImportProgress); } catch(err) { alert('Extraction failed: ' + err.message); } finally { setImporting(false); setImportProgress(0); } } }}
              disabled={importing}
              style={{ flex:1, padding:0, borderRadius:12, border: importing ? 'none' : '1.5px dashed rgba(168,85,247,0.4)', color:'#a855f7', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxSizing:'border-box', overflow:'hidden', position:'relative', height:44, transition:'all 0.15s' }}>
              {importing ? (
                <>
                  <div style={{ position:'absolute', left:0, top:0, bottom:0, width:`${importProgress}%`, background:'linear-gradient(135deg,rgba(168,85,247,0.2),rgba(168,85,247,0.35))', transition:'width 0.3s ease', borderRadius:12 }} />
                  <span style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', gap:6 }}>
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:11 }}></i> {importProgress}%
                  </span>
                </>
              ) : (
                <><i className="fa-solid fa-file-import" style={{ fontSize:11 }}></i> Import</>
              )}
            </button>
            <button onClick={()=>setShowImportHelp(!showImportHelp)} style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:'#a855f7', border:'none', color:'#fff', fontSize:10, fontWeight:900, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>?</button>
          </div>
        </div>

        {/* ── Imported Events Review Panel ── */}
        {importedEvents.length > 0 && (
          <div style={{ border:'1px solid rgba(168,85,247,0.3)', borderRadius:14, background:'var(--bg3)', marginBottom:16 }}>
            <div style={{ padding:'12px 14px', background:'rgba(168,85,247,0.08)', borderBottom:'1px solid rgba(168,85,247,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
              <p style={{ fontSize:13, fontWeight:700, color:'#a855f7', margin:0 }}>
                <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight:6 }}></i>
                {importedEvents.length} event{importedEvents.length !== 1 ? 's' : ''}
              </p>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => { setImportedEvents([]); setImportedPdfData([]); }}
                  style={{ fontSize:12, fontWeight:700, color:'var(--text3)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:'6px 8px', minHeight:36 }}>
                  Discard
                </button>
                <button onClick={() => {
                  const cleaned = importedEvents.map(({ _key, ...rest }) => rest);
                  addExtracted(cleaned, importedPdfData);
                  setImportedEvents([]); setImportedPdfData([]);
                }}
                  style={{ fontSize:12, fontWeight:700, color:'white', background:'#a855f7', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontFamily:'inherit', minHeight:36 }}>
                  Add All {importedEvents.length}
                </button>
              </div>
            </div>
            <div style={{ padding:'8px 12px', maxHeight: isMobile ? 280 : 400, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
              {importedEvents.map(e => {
                const color = e.travelType ? (({ flight_departure:'#1E40AF', flight_arrival:'#22c55e', hotel:'#06B6D4', carservice:'#06B6D4' })[e.travelType] || '#94a3b8') : '#94a3b8';
                return (
                  <div key={e._key} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:10, cursor:'pointer', minHeight:44 }}
                    onClick={() => { setEditEvt(e); setShowEvent(true); }}>
                    <div style={{ width:3, height:32, borderRadius:2, background:color, flexShrink:0 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {e.timeStart ? fmt12(e.timeStart) + ': ' : ''}{e.title}
                      </p>
                      <p style={{ fontSize:11, color:'var(--text3)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {e.date || ''}{e.location ? ' · ' + e.location : ''}{e.person ? ' · ' + e.person : ''}
                      </p>
                    </div>
                    <button title="Add to sheet" onClick={ev => { ev.stopPropagation(); const { _key, ...rest } = e; addExtracted([rest], importedPdfData); setImportedEvents(prev => prev.filter(x => x._key !== e._key)); }}
                      style={{ background:'rgba(168,85,247,0.15)', border:'none', color:'#a855f7', cursor:'pointer', fontSize:13, padding:'4px 8px', flexShrink:0, minWidth:32, minHeight:32, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>+</button>
                    <button title="Remove" onClick={ev => { ev.stopPropagation(); setImportedEvents(prev => prev.filter(x => x._key !== e._key)); }}
                      style={{ background:'none', border:'none', color:'var(--text3)', cursor:'pointer', fontSize:16, padding:'8px', flexShrink:0, minWidth:32, minHeight:32, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Import Help Panel ── */}
        {showImportHelp && (
          <Card style={{ padding:16, marginBottom:16, background:'rgba(168,85,247,0.06)', border:'1px solid rgba(168,85,247,0.2)' }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize:14, color:'#a855f7', marginTop:2 }}></i>
              <div>
                <p style={{ fontSize:13, fontWeight:700, color:'#a855f7', marginBottom:4 }}>AI-Powered Import</p>
                <p style={{ fontSize:12, color:'var(--text2)', lineHeight:1.5, margin:0 }}>
                  Drop a PDF, screenshot, or image of a schedule and AI will extract events automatically. Works with itineraries, call sheets, venue schedules, and more.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* ── Source Buttons: Google Calendar | Travel | Library ── */}
        <div style={{ display:'flex', gap: isMobile ? 6 : 10, marginBottom: sourceTab ? 10 : 16 }}>
          {[
            { key:'gcal',    icon:null, img:'https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png', label: isMobile ? 'GCal' : 'Google Cal', count: gcalFiltered.length },
            { key:'travel',  icon:'fa-solid fa-plane', label:'Travel', count: travelRecords.length + gcalTravelParsed.filter(p=>!p.alreadyImported).length },
            { key:'library', icon:'fa-solid fa-clock-rotate-left', label:'Library', count: library.length },
          ].map(s => {
            const active = sourceTab === s.key;
            return (
              <button key={s.key} onClick={() => setSourceTab(active ? '' : s.key)}
                style={{ flex:1, padding: isMobile ? '10px 8px' : '12px 16px', borderRadius:12, background: active ? 'rgba(30,64,175,0.12)' : 'var(--card)', border: active ? '1.5px solid rgba(30,64,175,0.4)' : '1.5px solid var(--border)', color: active ? '#1E40AF' : 'var(--text2)', fontFamily:'inherit', fontSize: isMobile ? 12 : 13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap: isMobile ? 5 : 8, transition:'all 0.15s', minWidth:0 }}>
                {s.img ? <img src={s.img} alt="" style={{ width:14, height:14, flexShrink:0 }} /> : <i className={s.icon} style={{ fontSize:11, flexShrink:0 }}></i>}
                {s.label}
                {s.count > 0 && <span style={{ fontSize:9, fontWeight:700, color:'#1E40AF', background:'rgba(30,64,175,0.15)', padding:'1px 5px', borderRadius:6 }}>{s.count}</span>}
                <i className={`fa-solid fa-chevron-${active ? 'up' : 'down'}`} style={{ fontSize:8, opacity:0.5, flexShrink:0 }}></i>
              </button>
            );
          })}
        </div>

        {/* ── Dropdown Panel ── */}
        {sourceTab && <Card style={{ padding:0, overflow:'hidden', marginBottom:16, borderTop:'2px solid rgba(30,64,175,0.4)' }}>

          {/* Google Calendar tab */}
          {sourceTab === 'gcal' && (
            <div style={{ padding:0 }}>
              {!gcalConnected ? (
                <div style={{ padding:'28px 16px', textAlign:'center' }}>
                  <img src="https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png" alt="" style={{ width:32, height:32, display:'block', margin:'0 auto 10px' }} />
                  <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', marginBottom:6 }}>Connect Google Calendar</p>
                  <p style={{ fontSize:12, color:'var(--text3)', marginBottom:16, lineHeight:1.5 }}>Sign in with Google to automatically pull<br/>events into day sheets</p>
                  <button onClick={async () => {
                    try {
                      const d = await apiFetch(`/calendar/connect/${selArtist}`);
                      if (d.url) window.location.href = d.url;
                    } catch(e) { alert(e.message); }
                  }} style={{ padding:'11px 24px', borderRadius:10, background:'#1E40AF', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:8, boxShadow:'0 4px 12px rgba(30,64,175,0.3)' }}>
                    <img src="https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png" alt="" style={{ width:14, height:14 }} />Connect Calendar
                  </button>
                </div>
              ) : gcalLoading ? (
                <div style={{ textAlign:'center', padding:24, color:'var(--text2)', fontSize:13 }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ marginRight:6 }}></i>Loading calendar...
                </div>
              ) : gcalFiltered.length === 0 ? (
                <div style={{ padding:24, textAlign:'center' }}>
                  <img src="https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_31_2x.png" alt="" style={{ width:24, height:24, display:'block', margin:'0 auto 8px' }} />
                  <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>No calendar events for {dateShort}</p>
                </div>
              ) : (
                gcalFiltered.map((ge, idx) => {
                  const key = ge.gcalId||ge.title;
                  const isAllDay = ge.isAllDay || !ge.timeStart;
                  const added = isAllDay ? notes.includes(ge.title) : events.some(e => e.title === ge.title);
                  return (
                    <div key={key} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom: idx < gcalFiltered.length-1 ? '0.5px solid rgba(51,65,85,0.5)' : 'none', opacity: added ? 0.6 : 1, background: added ? 'rgba(34,197,94,0.03)' : 'transparent' }}>
                      <div style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background: isAllDay ? 'rgba(245,158,11,0.1)' : 'rgba(30,64,175,0.1)' }}>
                        <i className={isAllDay ? 'fa-solid fa-thumbtack' : 'fa-solid fa-calendar'} style={{ fontSize:12, color: isAllDay ? '#f59e0b' : '#1E40AF' }}></i>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ge.title}</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>
                          {isAllDay ? 'All day \u2192 notes' : ge.timeStart ? fmt12(ge.timeStart) : ''}
                          {!isAllDay && ge.timeEnd ? ` \u2013 ${fmt12(ge.timeEnd)}` : ''}
                          {ge.calendar ? ` \u00b7 ${ge.calendar}` : ''}
                        </div>
                      </div>
                      {added ? (
                        <>
                          <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', flexShrink:0 }}>
                            <i className="fa-solid fa-check" style={{ fontSize:8, marginRight:2 }}></i>On sheet
                          </span>
                          <button onClick={() => removeGcalEvent(ge)} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                            <i className="fa-solid fa-xmark" style={{ fontSize:12, color:'#ef4444' }}></i>
                          </button>
                        </>
                      ) : (
                        <button onClick={() => addGcalEvent(ge)} style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#1E40AF', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                          <i className="fa-solid fa-plus" style={{ fontSize:8, marginRight:2 }}></i>Add
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Travel tab */}
          {sourceTab === 'travel' && (
            <div style={{ padding:0 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, padding:'10px 16px', borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
                <button onClick={() => setShowInlineTravel(true)} style={{ padding:'8px 12px', borderRadius:10, background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.25)', color:'#1E40AF', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                  <i className="fa-solid fa-plus" style={{ fontSize:9 }}></i> Add Travel
                </button>
                <label htmlFor="import-file-input"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='rgba(30,64,175,0.6)'; e.currentTarget.style.background='rgba(30,64,175,0.08)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor='rgba(30,64,175,0.3)'; e.currentTarget.style.background='transparent'; }}
                  onDrop={async e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.borderColor='rgba(30,64,175,0.3)'; e.currentTarget.style.background='transparent'; if (e.dataTransfer.files?.length) { setImporting(true); try { await handleImportFiles(Array.from(e.dataTransfer.files)); } catch(err) { alert('Extraction failed: ' + err.message); } finally { setImporting(false); } } }}
                  style={{ padding:'8px 12px', borderRadius:10, background:'transparent', border:'1.5px dashed rgba(30,64,175,0.3)', color:'#1E40AF', fontFamily:'inherit', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6, transition:'all 0.15s' }}>
                  <i className="fa-solid fa-file-import" style={{ fontSize:9 }}></i> {importing ? 'Extracting...' : 'Import Travel Itinerary'}
                </label>
              </div>

              {/* GCal travel scanning indicator */}
              {gcalTravelLoading && (
                <div style={{ padding:'12px 16px', fontSize:12, color:'var(--text3)', display:'flex', alignItems:'center', gap:6, borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:10 }}></i> Scanning calendar for travel...
                </div>
              )}

              {/* Detected GCal travel */}
              {gcalTravelParsed.filter(p=>!p.alreadyImported).map((p, i) => {
                const color = travelColors[p.type] || 'var(--blue)';
                const title = p.type==='flight' ? `${p.airline||''} ${p.flightNumber||''}`.trim()||'Flight' : p.hotelName||p.carServiceCompany||p.driverName||'Travel';
                const route = p.type==='flight' ? `${p.departureAirport||'?'} \u2192 ${p.arrivalAirport||'?'}` : '';
                const alreadyOnSheet = travelRecords.some(r => r.flightNumber && r.flightNumber === p.flightNumber);
                return (
                  <div key={p.gcalEventId||`gct-${i}`} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
                    <div style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background:`${color}15` }}>
                      <i className={`fa-solid ${travelIcons[p.type]||'fa-plane'}`} style={{ fontSize:12, color }}></i>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{title}{route ? <span style={{ color, marginLeft:4 }}>{route}</span> : ''}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}><i className="fab fa-google" style={{ fontSize:8, marginRight:3 }}></i>from calendar</div>
                    </div>
                    {alreadyOnSheet ? (
                      <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', flexShrink:0 }}>
                        <i className="fa-solid fa-check" style={{ fontSize:8, marginRight:2 }}></i>On sheet
                      </span>
                    ) : (
                      <button onClick={async () => {
                        setGcalTravelImporting(p.gcalEventId || i);
                        try {
                          const body = { artistId: selArtist, type: p.type||'flight', startDate: p.startDate||date, endDate: p.endDate||date,
                            airline:p.airline||null, flightNumber:p.flightNumber||null, departureAirport:p.departureAirport||null, arrivalAirport:p.arrivalAirport||null,
                            flightDepartureTime:p.flightDepartureTime||null, flightArrivalTime:p.flightArrivalTime||null, seat:p.seat||null, flightConfirmation:p.flightConfirmation||null,
                            passenger:p.passenger||null, flightType:p.flightType||'commercial', tailNumber:p.tailNumber||null, aircraftType:p.aircraftType||null, operator:p.operator||null,
                            hotelName:p.hotelName||null, hotelAddress:p.hotelAddress||null, hotelConfirmation:p.hotelConfirmation||null,
                            carServiceCompany:p.carServiceCompany||null, carServiceDriver:p.carServiceDriver||null, carServicePhone:p.carServicePhone||null,
                            carServicePickup:p.carServicePickup||null, carServiceDropoff:p.carServiceDropoff||null,
                            driverName:p.driverName||null, driverPhone:p.driverPhone||null, notes:p.notes||null };
                          const result = await api.createTravel(body);
                          if (result?.id) {
                            setTravelRecords(prev => [...prev, result]);
                            setGcalTravelParsed(prev => prev.map(g => (g.gcalEventId||g)===(p.gcalEventId||p) ? {...g, alreadyImported:true} : g));
                            if (result.events?.length) {
                              for (const evt of result.events) { if (evt.id) setEvents(prev => [...prev, evt]); }
                            }
                          }
                        } catch(e) { console.error('GCal travel import:', e); }
                        finally { setGcalTravelImporting(null); }
                      }}
                        disabled={gcalTravelImporting && gcalTravelImporting === (p.gcalEventId||i)}
                        style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#1E40AF', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                        {(gcalTravelImporting && gcalTravelImporting===(p.gcalEventId||i)) ? <i className="fa-solid fa-spinner fa-spin" style={{ fontSize:9 }}></i> : <><i className="fa-solid fa-plus" style={{ fontSize:8, marginRight:2 }}></i>Add</>}
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Existing travel records */}
              {travelRecords.map((r, idx) => {
                const title = r.type==='flight'
                  ? `${r.passenger || artistObj?.name || 'Flight'} ${r.departureAirport||''} \u2192 ${r.arrivalAirport||''}`.trim()
                  : r.type==='carservice' ? r.carServiceCompany||'Car Service'
                  : r.hotelName||r.carCompany||r.driverName||r.trainCompany||r.type;
                const sub = r.type==='flight'
                  ? `${r.airline||''} ${r.flightNumber||''}`.trim()
                  : r.type==='carservice' ? `${r.carServiceDriver||''} ${r.carServiceTime ? ' \u00b7 '+r.carServiceTime : ''}`.trim()
                  : r.startDate+(r.endDate!==r.startDate ? ' to '+r.endDate : '');
                const onSheet = events.some(e => e.travelId === r.id);
                return (
                  <div key={r.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom: idx < travelRecords.length-1 ? '0.5px solid rgba(51,65,85,0.5)' : 'none', cursor:'pointer' }}
                    onClick={() => setEditTravelRecord(r)}>
                    <div style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background:`${travelColors[r.type]||'var(--text3)'}15` }}>
                      <i className={`fa-solid ${travelIcons[r.type]||'fa-suitcase'}`} style={{ fontSize:12, color:travelColors[r.type]||'var(--text3)' }}></i>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}>{sub}</div>
                    </div>
                    {onSheet ? (
                      <>
                        <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', flexShrink:0 }}>
                          <i className="fa-solid fa-check" style={{ fontSize:8, marginRight:2 }}></i>On sheet
                        </span>
                        <button onClick={(ev) => { ev.stopPropagation(); setEvents(prev => prev.filter(e => e.travelId !== r.id)); }} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                          <i className="fa-solid fa-xmark" style={{ fontSize:12, color:'#ef4444' }}></i>
                        </button>
                      </>
                    ) : (
                      <button onClick={async (ev) => {
                        ev.stopPropagation();
                        try {
                          // Check for existing events linked to this travel record
                          const res = await api.getEventsByArtist(selArtist, date);
                          const allEvts = res?.events || res || [];
                          const travelEvts = allEvts.filter(e => e.travelId === r.id);
                          if (travelEvts.length > 0) {
                            for (const evt of travelEvts) {
                              if (!events.some(e => e.id === evt.id)) setEvents(prev => [...prev, evt]);
                            }
                          } else {
                            // No linked events — re-save travel record to trigger server-side event generation
                            const result = await apiFetch(`/travel/${r.id}`, { method:'PATCH', body: JSON.stringify({ regenerateEvents: true }) });
                            if (result?.events?.length) {
                              setEvents(prev => [...prev, ...result.events]);
                            } else {
                              // Fallback: re-fetch all events
                              const res2 = await api.getEventsByArtist(selArtist, date);
                              const evts2 = (res2?.events || res2 || []).filter(e => e.travelId === r.id);
                              for (const evt of evts2) {
                                if (!events.some(e => e.id === evt.id)) setEvents(prev => [...prev, evt]);
                              }
                            }
                          }
                        } catch(e) { console.error('Add travel events:', e); }
                      }}
                        style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#1E40AF', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                        <i className="fa-solid fa-plus" style={{ fontSize:8, marginRight:2 }}></i>Add
                      </button>
                    )}
                  </div>
                );
              })}

              {travelRecords.length === 0 && gcalTravelParsed.filter(p=>!p.alreadyImported).length === 0 && !gcalTravelLoading && (
                <div style={{ padding:24, textAlign:'center' }}>
                  <i className="fa-solid fa-suitcase-rolling" style={{ fontSize:24, color:'#a855f7', display:'block', marginBottom:8 }}></i>
                  <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>No travel for {dateShort}</p>
                </div>
              )}
            </div>
          )}

          {/* Library tab */}
          {sourceTab === 'library' && (
            <div style={{ padding:0 }}>
              <div style={{ padding:'10px 16px', borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
                <input value={libSearch} onChange={e=>setLibSearch(e.target.value)} placeholder="Search past events..."
                  style={{ width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8, padding:'8px 12px', fontSize:13, color:'var(--text)', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }} />
                <div style={{ display:'flex', gap:6, marginTop:8 }}>
                  {['recent','frequent'].map(t => (
                    <button key={t} onClick={()=>setLibTab(t)}
                      style={{ fontSize:11, padding:'3px 10px', borderRadius:8, border:`1px solid ${libTab===t?'rgba(30,64,175,0.4)':'var(--border)'}`, background:libTab===t?'rgba(30,64,175,0.1)':'var(--bg)', color:libTab===t?'#1E40AF':'var(--text3)', cursor:'pointer', fontFamily:'inherit', fontWeight:700, textTransform:'capitalize' }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                let items = library;
                if (libSearch) items = items.filter(e => e.title?.toLowerCase().includes(libSearch.toLowerCase()));
                if (libTab === 'frequent') items = [...items].sort((a,b) => (b.count||0) - (a.count||0));
                if (items.length === 0) return (
                  <div style={{ padding:24, textAlign:'center' }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize:24, color:'#1E40AF', display:'block', marginBottom:8 }}></i>
                    <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>No past events found</p>
                  </div>
                );
                return items.slice(0,20).map((e,i) => {
                  const already = events.some(ev => ev.title === e.title);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom: i < Math.min(items.length,20)-1 ? '0.5px solid rgba(51,65,85,0.5)' : 'none', opacity: already ? 0.5 : 1 }}>
                      <div style={{ width:30, height:30, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background:'rgba(30,64,175,0.1)' }}>
                        <i className="fa-solid fa-clock-rotate-left" style={{ fontSize:12, color:'#1E40AF' }}></i>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.title}</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>
                          {e.timeStart ? fmt12(e.timeStart) : 'All day'}
                          {e.person ? ` \u00b7 ${e.person.split(',')[0]}` : ''}
                          {e.count > 1 ? ` \u00b7 ${e.count}x` : ''}
                        </div>
                      </div>
                      {already ? (
                        <>
                          <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', flexShrink:0 }}>
                            <i className="fa-solid fa-check" style={{ fontSize:8, marginRight:2 }}></i>Added
                          </span>
                          <button onClick={() => setEvents(prev => prev.filter(ev => ev.title !== e.title))} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                            <i className="fa-solid fa-xmark" style={{ fontSize:12, color:'#ef4444' }}></i>
                          </button>
                        </>
                      ) : (
                        <button onClick={() => {
                          const newEvt = { title:e.title, person:e.person||'', date, timeStart:e.timeStart||null, timeEnd:e.timeEnd||null, location:e.location||null, notes:e.notes||null, url:e.url||null };
                          handleSaveEvent(newEvt);
                        }}
                          style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:8, color:'#1E40AF', background:'rgba(30,64,175,0.1)', border:'1px solid rgba(30,64,175,0.2)', cursor:'pointer', fontFamily:'inherit', flexShrink:0 }}>
                          <i className="fa-solid fa-plus" style={{ fontSize:8, marginRight:2 }}></i>Add
                        </button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </Card>}

        {/* ── Day Sheet Preview ── */}
        <Card style={{ padding:0, overflow:'hidden', marginBottom:16 }}>
          <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:'rgba(34,197,94,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="fa-solid fa-clipboard-list" style={{ fontSize:14, color:'#22c55e' }}></i>
              </div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>Day Sheet Preview</div>
                <div style={{ fontSize:11, color:'var(--text3)' }}>{events.length} event{events.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          </div>

          {/* Travel quick cards — only show for travel that's on the sheet */}
          {travelRecords.filter(r => events.some(e => e.travelId === r.id)).length > 0 && (
            <div style={{ padding:'12px 16px', borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
              <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                {(() => {
                  const onSheetTravel = travelRecords.filter(r => events.some(e => e.travelId === r.id));
                  const flights = onSheetTravel.filter(r => r.type === 'flight');
                  const others = onSheetTravel.filter(r => r.type !== 'flight');
                  const flightsByPassenger = {};
                  flights.forEach(f => {
                    const key = f.passenger || artistObj?.name || 'Flight';
                    if (!flightsByPassenger[key]) flightsByPassenger[key] = [];
                    flightsByPassenger[key].push(f);
                  });
                  const tCard = (key, icon, color, label, sub, count) => (
                    <div key={key} style={{ flex:'1 1 0', maxWidth:140, minWidth:100, padding:12, borderRadius:12, border:`1.5px solid ${color}30`, background:`${color}06`, display:'flex', flexDirection:'column', alignItems:'center', gap:4, position:'relative', cursor:'pointer', transition:'all 0.15s' }}>
                      {count > 1 && <div style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:color, color:'#0f172a', fontSize:10, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center' }}>{count}</div>}
                      <i className={`fa-solid ${icon}`} style={{ fontSize:18, color }}></i>
                      <span style={{ fontSize:9, fontWeight:800, color }}>{label}</span>
                      {sub && <span style={{ fontSize:7, fontWeight:800, color:'#22c55e' }}>{sub}</span>}
                    </div>
                  );
                  const cards = [];
                  others.filter(r => r.type === 'hotel').forEach(r => cards.push(tCard(r.id, 'fa-hotel', '#06B6D4', r.hotelName || 'Hotel')));
                  Object.entries(flightsByPassenger).forEach(([passenger, pFlights]) => {
                    const f0 = pFlights[0];
                    const ini = flightInitials(passenger, f0.leadPassenger, artists);
                    if (pFlights.length === 1) {
                      cards.push(tCard(f0.id, 'fa-plane', '#1E40AF', `${f0.departureAirport||'?'} \u2192 ${f0.arrivalAirport||'?'}`, ini));
                    } else {
                      cards.push(tCard(`flights-${passenger}`, 'fa-plane', '#1E40AF', `${pFlights.length} flights`, ini, pFlights.length));
                    }
                  });
                  others.filter(r => r.type === 'carservice').forEach(r => cards.push(tCard(r.id, 'fa-car-side', '#06B6D4', r.carServiceCompany || r.carServiceDriver || 'Car')));
                  others.filter(r => r.type === 'ground').forEach(r => cards.push(tCard(r.id, 'fa-van-shuttle', '#f472b6', r.driverName || 'Ground')));
                  others.filter(r => r.type === 'car').forEach(r => cards.push(tCard(r.id, 'fa-car', '#fbbf24', r.carCompany || 'Rental')));
                  others.filter(r => r.type === 'train').forEach(r => cards.push(tCard(r.id, 'fa-train', '#a78bfa', r.trainCompany || 'Train')));
                  return cards;
                })()}
              </div>
            </div>
          )}

          {/* Notes */}
          <div style={{ padding:'12px 16px', borderBottom:'0.5px solid rgba(51,65,85,0.5)' }}>
            <label style={{ fontSize:11, fontWeight:700, color:'rgba(245,158,11,0.5)', letterSpacing:0.5, display:'flex', alignItems:'center', gap:5, marginBottom:6 }}><i className="fa-solid fa-thumbtack" style={{ fontSize:9 }}></i> NOTES / ANNOUNCEMENTS</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
              placeholder="**Bold** for announcements. e.g. **Chris & Spencer on site today**"
              style={{ width:'100%', background:'rgba(245,158,11,0.02)', border:'1px solid rgba(245,158,11,0.12)', borderLeft:'2px solid rgba(245,158,11,0.35)', borderRadius:'0 10px 10px 0', padding:'10px 12px', fontSize:13, color:'rgba(251,191,36,0.7)', outline:'none', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }} />
          </div>

          {/* Events list */}
          {sortedEvents.length === 0 ? (
            <div style={{ textAlign:'center', padding:'32px 16px', color:'var(--text3)' }}>
              <i className="fa-regular fa-clipboard" style={{ fontSize:32, display:'block', marginBottom:8, color:'var(--text3)' }}></i>
              <p style={{ fontSize:13, margin:0 }}>No events yet — add from calendar, import, or create manually</p>
            </div>
          ) : (
            sortedEvents.map((e, idx) => (
              <div key={e.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', borderBottom: idx < sortedEvents.length-1 ? '0.5px solid rgba(51,65,85,0.5)' : 'none' }}>
                <div style={{ width:3, height:32, borderRadius:2, background: e.travelType ? travelColors[e.travelType] || 'var(--blue)' : accentColor, flexShrink:0 }} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.title}</div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>
                    {e.timeStart ? fmt12(e.timeStart) : 'TBD'}
                    {(isPastMidnight(e.timeStart) || e.nextDay) && <span style={{ fontSize:9, color:'var(--text3)', opacity:0.7 }}> (+1)</span>}
                    {e.timeEnd ? ` \u2013 ${fmt12(e.timeEnd)}` : ''}
                    {e.timeEnd && isPastMidnight(e.timeEnd) && !isPastMidnight(e.timeStart) && <span style={{ fontSize:9, color:'var(--text3)', opacity:0.7 }}> (+1)</span>}
                    {e.person ? ` \u00b7 ${e.person.split(',')[0]}` : ''}
                    {e.location ? ` \u00b7 ${e.location}` : ''}
                  </div>
                </div>
                <button onClick={() => { setEditEvt(e); setShowEvent(true); }} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                  <i className="fa-solid fa-pen" style={{ fontSize:11, color:'var(--text3)' }}></i>
                </button>
                <button onClick={() => setEvents(prev => prev.filter(x => x.id !== e.id))} style={{ background:'none', border:'none', cursor:'pointer', padding:4, flexShrink:0 }}>
                  <i className="fa-solid fa-xmark" style={{ fontSize:12, color:'#ef4444' }}></i>
                </button>
              </div>
            ))
          )}
        </Card>

        {/* ── Bottom Actions ── */}
        <div style={{ display:'flex', gap:10, marginBottom:20 }}>
          {isMobile && (
            <button onClick={handleSaveDraft} disabled={saving||!selArtist}
              style={{ padding:'16px 20px', borderRadius:14, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <i className="fa-solid fa-save" style={{ fontSize:13 }}></i> Draft
            </button>
          )}
          <button onClick={async () => {
            if (!city) { setShowLocationPrompt(true); return; }
            // Save first so the review modal opens against a real sheet ID,
            // then attach the artist (the modal reads distribution lists off
            // it). The modal handles publish + send in a single click; the
            // old inline preview/distribution screen is preserved as a
            // fallback path but is no longer the primary CTA.
            setSaving(true);
            try {
              const sheetId = await ensureSaved();
              if (!sheetId) return;
              setReviewSheet({ id: sheetId, date, status: status || 'draft', notes, city, artistId: selArtist, artistIds: selArtistIds, artist: artistObj });
              setShowReviewModal(true);
            } finally { setSaving(false); }
          }} disabled={events.length === 0 || saving} style={{ flex:1, padding:'16px 20px', borderRadius:14, background: events.length > 0 ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--card)', border: events.length > 0 ? 'none' : '1px solid var(--border)', color: events.length > 0 ? '#fff' : 'var(--text3)', fontFamily:'inherit', fontSize:15, fontWeight:800, cursor: events.length > 0 ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', gap:10, boxShadow: events.length > 0 ? '0 4px 16px rgba(30,64,175,0.3)' : 'none', transition:'all 0.2s' }}>
            <i className="fa-solid fa-eye" style={{ fontSize:14 }}></i> Review & Send
          </button>
        </div>

      </div>


      {/* ── Hidden DropZone for programmatic import ── */}
      <div style={{ display:'none' }}>
        <DropZone onFiles={(evts, pdfs) => addExtracted(evts, pdfs)} />
      </div>

      {/* ── Modals ── */}
      {showEvent && (
        <EventModal event={editEvt} onSave={handleSaveEvent} onSaveAndSync={handleSaveAndSync} onClose={() => { setShowEvent(false); setEditEvt(null); }} sheetArtistId={selArtist} sheetDate={date} />
      )}
      {showInlineTravel && (
        <TravelFormModal record={null} artistId={selArtist} artists={artists} defaultDate={date} onSave={async (result) => {
          syncTravelEvents(result);
          if (result.events?.length) {
            if (id) {
              for (const evt of result.events) {
                if (evt.id) { await api.linkEvent(evt.id, id); }
              }
              try {
                const sheet = await api.getSheet(id);
                if (sheet?.events) setEvents(sheet.events.map(e => ({...e, isNew:false})));
              } catch {}
            } else {
              for (const evt of result.events) {
                setEvents(prev => [...prev, evt]);
              }
            }
          }
          if (selArtist && date) {
            try { const t = await api.getTravel(selArtist, date); setTravelRecords(Array.isArray(t)?t:[]); } catch {}
          }
          setShowInlineTravel(false);
        }} onClose={() => setShowInlineTravel(false)} />
      )}
      {/* Extracted travel from import — opens TravelFormModal pre-filled */}
      {extractedTravelQueue.length > 0 && extractedTravelIdx < extractedTravelQueue.length && (
        <TravelFormModal
          record={extractedTravelQueue[extractedTravelIdx]}
          artistId={selArtist}
          artists={artists}
          defaultDate={date}
          onSave={async (result) => {
            syncTravelEvents(result);
            if (result.events?.length) {
              if (id) {
                for (const evt of result.events) {
                  if (evt.id) { await api.linkEvent(evt.id, id); }
                }
                // Re-fetch sheet to get properly linked events with regenerated titles
                try {
                  const sheet = await api.getSheet(id);
                  if (sheet?.events) setEvents(sheet.events.map(e => ({...e, isNew:false})));
                } catch {}
              } else {
                // New sheet — just add events to local state
                for (const evt of result.events) {
                  setEvents(prev => [...prev, evt]);
                }
              }
            }
            // Refresh travel list
            if (selArtist && date) {
              try { const t = await api.getTravel(selArtist, date); setTravelRecords(Array.isArray(t)?t:[]); } catch {}
            }
            const nextIdx = extractedTravelIdx + 1;
            if (nextIdx < extractedTravelQueue.length) {
              setExtractedTravelIdx(nextIdx);
            } else {
              setExtractedTravelQueue([]);
              setExtractedTravelIdx(0);
            }
          }}
          onClose={() => { setExtractedTravelQueue([]); setExtractedTravelIdx(0); }}
        />
      )}
      {editTravelRecord && (
        <TravelFormModal
          record={editTravelRecord}
          artistId={selArtist}
          artists={artists}
          defaultDate={date}
          onSave={async (result) => {
            syncTravelEvents(result);
            // Re-fetch sheet to get server-regenerated titles with lead passenger
            if (id) {
              try {
                const sheet = await api.getSheet(id);
                if (sheet?.events) setEvents(sheet.events.map(e => ({...e, isNew:false})));
                if (sheet?.travel) setTravelRecords(sheet.travel);
              } catch {}
            }
            // Also refresh travel records
            if (selArtist && date) {
              try { const t = await api.getTravel(selArtist, date); setTravelRecords(Array.isArray(t)?t:[]); } catch {}
            }
            setEditTravelRecord(null);
          }}
          onClose={() => setEditTravelRecord(null)}
        />
      )}
      {flightInfoModal && (
        <FlightInfoModal
          events={flightInfoModal.events}
          pdfFiles={flightInfoModal.pdfFiles}
          onConfirm={(info, passengerMap) => confirmFlightInfo(flightInfoModal.events, flightInfoModal.pdfFiles, info, passengerMap)}
          onClose={() => setFlightInfoModal(null)}
        />
      )}

      {/* ── Location Prompt ── */}
      {showLocationPrompt && (
        <div style={{ position:'fixed', inset:0, zIndex:1001, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
          onMouseDown={e => e.target === e.currentTarget && setShowLocationPrompt(false)}>
          <div style={{ background:'var(--card)', borderRadius:16, padding:24, maxWidth:400, width:'100%', border:'1px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ width:40, height:40, borderRadius:10, background:'rgba(245,158,11,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="fa-solid fa-location-dot" style={{ fontSize:16, color:'#f59e0b' }}></i>
              </div>
              <div>
                <p style={{ fontSize:16, fontWeight:800, color:'var(--text)', margin:0 }}>No Starting Location</p>
                <p style={{ fontSize:13, color:'var(--text3)', margin:'2px 0 0' }}>Weather won't be available without a location.</p>
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setShowLocationPrompt(false); }}
                style={{ flex:1, padding:'12px', borderRadius:10, background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                <i className="fa-solid fa-arrow-left" style={{ fontSize:10, marginRight:6 }}></i>Go Back
              </button>
              <button onClick={() => {
                setShowLocationPrompt(false);
                setSelectedEmailGroups([]);
                setPublishExcluded(new Set());
                setPublishExpandedGroup(null);
                setPublishExtraEmails([]);
                setShowPreview(true);
              }}
                style={{ flex:1, padding:'12px', borderRadius:10, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                Skip & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview & Publish Overlay ── */}
      {showPreview && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.7)', backdropFilter:'blur(8px)', display:'flex', flexDirection:'column', alignItems:'center', overflowY:'auto' }}>
          {/* Close bar */}
          <div style={{ width:'100%', maxWidth:620, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <span style={{ fontSize:13, fontWeight:700, color:'#94a3b8' }}>Preview</span>
            <button onClick={() => setShowPreview(false)} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', width:32, height:32, borderRadius:'50%', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          {/* Day Sheet Preview */}
          <div style={{ width:'100%', maxWidth:620, padding:'0 16px', flexShrink:0 }}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius:'16px 16px 0 0', overflow:'hidden', border:'1px solid var(--border)', borderBottom:'none' }}>
              <div style={{ height:4, background:accentColor }} />
              <div style={{ padding:'24px 24px 20px', textAlign:'center' }}>
                {selArtistIds.length > 1 ? (
                  <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:10 }}>
                    {selArtistIds.map(id => {
                      const a = artists.find(x => x.id === id);
                      if (!a?.photo) return null;
                      return (
                        <div key={id} style={{ width:50, height:50, borderRadius:'50%', overflow:'hidden', border:`3px solid ${a.color || accentColor}40`, flexShrink:0 }}>
                          <img src={a.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        </div>
                      );
                    })}
                  </div>
                ) : artistObj?.photo ? (
                  <div style={{ margin:'0 auto 10px', width:56, height:56, borderRadius:'50%', overflow:'hidden', border:`3px solid ${accentColor}40` }}>
                    <img src={artistObj.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>
                ) : artistObj?.emoji ? (
                  <div style={{ fontSize:36, marginBottom:8 }}>{artistObj.emoji}</div>
                ) : null}
                <p style={{ fontSize:22, fontWeight:900, color:'#fff', margin:0 }}>{selArtistIds.length > 1 ? selArtistIds.map(id => artists.find(a => a.id === id)?.name).filter(Boolean).join(' & ') : (artistObj?.name || 'Day Sheet')}</p>
                <p style={{ fontSize:14, color:'#94a3b8', marginTop:4 }}>{dateDisplay}</p>
                {city && <p style={{ fontSize:13, color:'#64748b', marginTop:2 }}><i className="fa-solid fa-location-dot" style={{ marginRight:4, fontSize:11 }}></i>{city}</p>}

                {/* Weather */}
                {weather && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:10, padding:'8px 16px', borderRadius:12, background:'linear-gradient(135deg,rgba(12,74,110,0.6),rgba(22,78,99,0.6))' }}>
                    <WeatherIcon condition={weather.condition} />
                    <span style={{ fontSize:18, fontWeight:800, color:'#fff' }}>{weather.tempHigh}°</span>
                    <span style={{ fontSize:12, color:'#94a3b8' }}>/ {weather.tempLow}°</span>
                    <span style={{ fontSize:11, color:'#cbd5e1' }}>{weather.condition}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Travel icon cards — between header and notes */}
            {travelRecords.filter(r => events.some(e => e.travelId === r.id)).length > 0 && (
              <div style={{ display:'flex', gap:8, padding:'12px 20px', justifyContent:'center', background:'var(--card)', borderLeft:'1px solid var(--border)', borderRight:'1px solid var(--border)' }}>
                {(() => {
                  const onSheet = travelRecords.filter(r => events.some(e => e.travelId === r.id));
                  const flights = onSheet.filter(r => r.type === 'flight');
                  const others = onSheet.filter(r => r.type !== 'flight');
                  const flightsByPassenger = {};
                  flights.forEach(f => {
                    const key = f.passenger || artistObj?.name || 'Flight';
                    if (!flightsByPassenger[key]) flightsByPassenger[key] = [];
                    flightsByPassenger[key].push(f);
                  });
                  const tCard = (key, icon, color, label, sub, count) => (
                    <div key={key} style={{ flex:'1 1 0', maxWidth:140, minWidth:90, padding:12, borderRadius:12, border:`1.5px solid ${color}30`, background:`${color}06`, display:'flex', flexDirection:'column', alignItems:'center', gap:4, position:'relative' }}>
                      {count > 1 && <div style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', background:color, color:'#0f172a', fontSize:10, fontWeight:900, display:'flex', alignItems:'center', justifyContent:'center' }}>{count}</div>}
                      <i className={`fa-solid ${icon}`} style={{ fontSize:18, color }}></i>
                      <span style={{ fontSize:9, fontWeight:800, color }}>{label}</span>
                      {sub && <span style={{ fontSize:7, fontWeight:800, color:'#22c55e' }}>{sub}</span>}
                    </div>
                  );
                  const cards = [];
                  others.filter(r => r.type === 'hotel').forEach(r => cards.push(tCard(r.id, 'fa-hotel', '#06B6D4', r.hotelName || 'Hotel')));
                  Object.entries(flightsByPassenger).forEach(([passenger, pFlights]) => {
                    const f0 = pFlights[0];
                    const ini = flightInitials(passenger, f0.leadPassenger, artists);
                    if (pFlights.length === 1) {
                      cards.push(tCard(f0.id, 'fa-plane', '#1E40AF', `${f0.departureAirport||'?'} \u2192 ${f0.arrivalAirport||'?'}`, ini));
                    } else {
                      cards.push(tCard(`flights-${passenger}`, 'fa-plane', '#1E40AF', `${pFlights.length} flights`, ini, pFlights.length));
                    }
                  });
                  others.filter(r => r.type === 'carservice').forEach(r => cards.push(tCard(r.id, 'fa-car-side', '#06B6D4', r.carServiceCompany || r.carServiceDriver || 'Car')));
                  others.filter(r => r.type === 'ground').forEach(r => cards.push(tCard(r.id, 'fa-van-shuttle', '#f472b6', r.driverName || 'Ground')));
                  others.filter(r => r.type === 'car').forEach(r => cards.push(tCard(r.id, 'fa-car', '#fbbf24', r.carCompany || 'Rental')));
                  others.filter(r => r.type === 'train').forEach(r => cards.push(tCard(r.id, 'fa-train', '#a78bfa', r.trainCompany || 'Train')));
                  return cards;
                })()}
              </div>
            )}

            {/* Notes */}
            {notes && (
              <div style={{ background:'var(--card)', borderLeft:'1px solid var(--border)', borderRight:'1px solid var(--border)', padding:'14px 20px' }}>
                <div style={{ background:'rgba(245,158,11,0.04)', borderLeft:'3px solid rgba(245,158,11,0.35)', borderRadius:'0 10px 10px 0', padding:'10px 14px' }}>
                  <p style={{ fontSize:13, color:'rgba(251,191,36,0.8)', margin:0, lineHeight:1.5, whiteSpace:'pre-wrap' }}>
                    {notes.replace(/\*\*(.*?)\*\*/g, '$1')}
                  </p>
                </div>
              </div>
            )}

            {/* Events */}
            <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderTop: notes ? 'none' : undefined, borderRadius: notes ? '0 0 16px 16px' : '0 0 16px 16px', overflow:'hidden' }}>
              {sortedEvents.map((e, idx) => {
                const showNextDayDivider = e.nextDay && (idx === 0 || !sortedEvents[idx-1].nextDay);
                return (<React.Fragment key={e.id}>
                {showNextDayDivider && (
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                    <div style={{ flex:1, height:1, background:'linear-gradient(to right, var(--border), transparent)' }} />
                    <span style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', whiteSpace:'nowrap' }}>NEXT DAY</span>
                    <div style={{ flex:1, height:1, background:'linear-gradient(to left, var(--border), transparent)' }} />
                  </div>
                )}
                <div onClick={() => { setEditEvt(e); setShowEvent(true); setShowPreview(false); }} style={{ display:'flex', alignItems:'stretch', borderBottom: idx < sortedEvents.length-1 ? '1px solid var(--border)' : 'none', cursor:'pointer', transition:'background 0.1s' }}
                  onMouseEnter={ev => ev.currentTarget.style.background='rgba(255,255,255,0.02)'}
                  onMouseLeave={ev => ev.currentTarget.style.background='transparent'}>
                  {(() => { const epc = findPersonColor(PERSON_COLORS, e.person?.split(',')[0]?.trim()); const eIsW = epc === '#e6edf3'; return (<>
                  <div style={{ width:4, flexShrink:0, background: e.travelType ? travelColors[e.travelType] || accentColor : eIsW ? accentColor : epc }} />
                  <div style={{ flex:1, padding:'14px 16px' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                      <div>
                        <p style={{ fontSize:15, fontWeight:800, color: eIsW ? 'var(--text)' : epc, margin:0, lineHeight:1.4 }}>
                          {e.timeStart && <><span style={{ textDecoration:'underline' }}>{fmt12(e.timeStart)}</span>{(isPastMidnight(e.timeStart) || e.nextDay) && <span style={{ fontSize:9, opacity:0.7 }}> (+1)</span>}: </>}
                          {(() => {
                            const p = e.person?.split(',')[0]?.trim();
                            const pLower = p?.toLowerCase() || '';
                            const isArtistOrCal = !p || ['everyone','all'].includes(pLower) || artists.some(a => a.name?.toLowerCase() === pLower) || pLower.includes('calendar');
                            return !isArtistOrCal ? <>{p} </> : null;
                          })()}
                          {e.title}
                        </p>
                        {(e.timeEnd || e.location) && <p style={{ fontSize:12, color:'var(--text3)', margin:'3px 0 0' }}>
                          {e.timeEnd && <>{fmt12(e.timeStart || '')} – {fmt12(e.timeEnd)}{isPastMidnight(e.timeEnd) && !isPastMidnight(e.timeStart) && <span style={{ fontSize:9, opacity:0.7 }}> (+1)</span>}</>}
                          {e.location && <>{e.timeEnd ? ' · ' : ''}<i className="fa-solid fa-location-dot" style={{ marginRight:4, fontSize:9 }}></i>{e.location}</>}
                        </p>}
                      </div>
                      <i className="fa-solid fa-pen" style={{ fontSize:10, color:'var(--text3)', marginTop:4, flexShrink:0 }}></i>
                    </div>
                  </div>
                  </>); })()}
                </div>
                </React.Fragment>);
              })}
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div style={{ width:'100%', maxWidth:620, padding: isMobile ? '20px 16px calc(40px + env(safe-area-inset-bottom, 0px))' : '20px 16px 32px', flexShrink:0 }}>
            {/* Email distribution — matches SendModal UI */}
            {(() => {
              const distContacts = artistObj?.distributionContacts || [];
              const distGroups = artistObj?.distributionGroups || [];
              // Build recipients from selected groups minus excluded
              const groupIncluded = distContacts.filter(c => {
                if (publishExcluded.has(c.id)) return false;
                const inGroup = (c.groupIds || []).some(gid => (selectedEmailGroups || []).includes(gid));
                const isUngrouped = !c.groupIds || c.groupIds.length === 0;
                return inGroup || (isUngrouped && (selectedEmailGroups || []).includes('ungrouped'));
              });
              const totalCount = groupIncluded.length + publishExtraEmails.length;

              return (
                <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 16px', marginBottom:12 }}>
                  {/* Header */}
                  <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase', margin:'0 0 10px' }}>
                    <i className="fa-solid fa-paper-plane" style={{ marginRight:4, fontSize:9, color:'var(--blue)' }}></i>Email Distribution
                  </p>

                  {/* Groups */}
                  {distGroups.length > 0 && (
                    <div style={{ marginBottom:12 }}>
                      <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                        <i className="fa-solid fa-folder" style={{ marginRight:4, fontSize:9 }}></i> Groups
                      </p>
                      <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {distGroups.map(g => {
                            const members = distContacts.filter(c => (c.groupIds || []).includes(g.id));
                            const activeCount = members.filter(c => !publishExcluded.has(c.id)).length;
                            const checked = (selectedEmailGroups || []).includes(g.id);
                            const isExpanded = publishExpandedGroup === g.id;
                            return (
                              <button key={g.id} onClick={() => {
                                setSelectedEmailGroups(prev => {
                                  const p = prev || [];
                                  return p.includes(g.id) ? p.filter(id => id !== g.id) : [...p, g.id];
                                });
                              }}
                                style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:20, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, border:'1.5px solid', transition:'all 0.15s',
                                  background: checked ? 'rgba(30,64,175,0.1)' : 'transparent',
                                  borderColor: checked ? 'var(--blue)' : 'var(--border)',
                                  color: checked ? 'var(--blue)' : 'var(--text3)' }}>
                                {checked && <i className="fa-solid fa-check" style={{ fontSize:9 }}></i>}
                                {g.name} <span style={{ opacity:0.6 }}>({activeCount}/{members.length})</span>
                                {members.length > 0 && (
                                  <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}
                                    onClick={e => { e.stopPropagation(); setPublishExpandedGroup(isExpanded ? null : g.id); }}
                                    style={{ fontSize:8, marginLeft:2, padding:'2px 4px', color: checked ? 'var(--blue)' : 'var(--text3)' }}></i>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Expanded group members */}
                        {publishExpandedGroup && (() => {
                          const members = publishExpandedGroup === 'ungrouped'
                            ? distContacts.filter(c => !c.groupIds || c.groupIds.length === 0)
                            : distContacts.filter(c => (c.groupIds || []).includes(publishExpandedGroup));
                          const groupName = publishExpandedGroup === 'ungrouped' ? 'Ungrouped' : distGroups.find(g => g.id === publishExpandedGroup)?.name;
                          if (!members.length) return null;
                          return (
                            <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'8px 0', marginTop:4, maxHeight:170, overflowY:'auto' }}>
                              <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', padding:'4px 14px 6px', margin:0, textTransform:'uppercase' }}>
                                {groupName}
                              </p>
                              {members.map(c => {
                                const included = !publishExcluded.has(c.id);
                                return (
                                  <button key={c.id} onClick={() => {
                                    setPublishExcluded(prev => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; });
                                  }}
                                    style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left', minHeight:44 }}>
                                    <div style={{ width:20, height:20, borderRadius:5, border: included ? '2px solid var(--blue)' : '2px solid var(--border)',
                                      background: included ? 'var(--blue)' : 'transparent',
                                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.12s' }}>
                                      {included && <i className="fa-solid fa-check" style={{ fontSize:9, color:'#fff' }}></i>}
                                    </div>
                                    <div style={{ flex:1, minWidth:0 }}>
                                      <p style={{ fontSize:13, fontWeight:600, color: included ? 'var(--text)' : 'var(--text3)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                        {c.name || c.email.split('@')[0]}
                                      </p>
                                      <p style={{ fontSize:11, color:'var(--text3)', margin:0 }}>{c.email}</p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Ungrouped / Individual Contacts */}
                  {(() => {
                    const ungrouped = distContacts.filter(c => !c.groupIds || c.groupIds.length === 0);
                    if (!ungrouped.length) return null;
                    return (
                      <div style={{ marginBottom:12 }}>
                        <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                          <i className="fa-solid fa-user" style={{ marginRight:4, fontSize:9 }}></i> Individual Contacts
                        </p>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                          {ungrouped.map(c => {
                            const isOn = (selectedEmailGroups || []).includes('ungrouped') && !publishExcluded.has(c.id);
                            return (
                              <button key={c.id} onClick={() => {
                                if (isOn) {
                                  setPublishExcluded(prev => { const n = new Set(prev); n.add(c.id); return n; });
                                } else {
                                  if (!(selectedEmailGroups || []).includes('ungrouped')) {
                                    setSelectedEmailGroups(prev => [...(prev || []), 'ungrouped']);
                                  }
                                  setPublishExcluded(prev => { const n = new Set(prev); n.delete(c.id); return n; });
                                }
                              }}
                                style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, border:'1.5px solid', transition:'all 0.12s',
                                  background: isOn ? 'rgba(30,64,175,0.1)' : 'transparent',
                                  borderColor: isOn ? 'var(--blue)' : 'var(--border)',
                                  color: isOn ? 'var(--blue)' : 'var(--text3)' }}>
                                {isOn && <i className="fa-solid fa-check" style={{ fontSize:8 }}></i>}
                                {c.name || c.email.split('@')[0]}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Main Team quick-select */}
                  {(() => {
                    const allGroupEmails = new Set();
                    distContacts.forEach(c => { if (c.email) allGroupEmails.add(c.email.toLowerCase()); });
                    const availUsers = publishAppUsers.filter(u => !allGroupEmails.has(u.email.toLowerCase()) && !publishExtraEmails.includes(u.email.toLowerCase()));
                    if (!availUsers.length) return null;
                    return (
                      <div style={{ marginBottom:12 }}>
                        <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                          <i className="fa-solid fa-user-group" style={{ marginRight:4, fontSize:9 }}></i> Main Team
                        </p>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                          {availUsers.map(u => (
                            <button key={u.email} onClick={() => {
                              if (!publishExtraEmails.includes(u.email)) setPublishExtraEmails(prev => [...prev, u.email]);
                            }}
                              style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600,
                                background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.15)', color:'var(--text)', transition:'all 0.12s' }}>
                              {u.photo ? (
                                <img src={u.photo} alt="" style={{ width:16, height:16, borderRadius:'50%', objectFit:'cover' }} />
                              ) : (
                                <i className="fa-solid fa-user" style={{ fontSize:8, color:'rgba(99,102,241,0.6)' }}></i>
                              )}
                              {u.name || u.email.split('@')[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* One-off emails */}
                  <div>
                    <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                      <i className="fa-solid fa-plus" style={{ marginRight:4, fontSize:8 }}></i> Add one-off
                    </p>
                    <div style={{ display:'flex', gap:6 }}>
                      <div style={{ flex:1 }}>
                        <EmailSearch value={publishExtraEmail} onChange={setPublishExtraEmail} onSelect={(email) => {
                          if (email && !publishExtraEmails.includes(email)) setPublishExtraEmails(prev => [...prev, email]);
                          setPublishExtraEmail('');
                        }} placeholder="Search contacts or type email..." />
                      </div>
                      <button onClick={() => {
                        const email = publishExtraEmail.trim().toLowerCase();
                        if (email.includes('@') && !publishExtraEmails.includes(email)) {
                          setPublishExtraEmails(prev => [...prev, email]);
                        }
                        setPublishExtraEmail('');
                      }}
                        style={{ padding:'8px 12px', borderRadius:8, background:'var(--blue)', border:'none', color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity: publishExtraEmail.trim().includes('@') ? 1 : 0.4 }}>
                        Add
                      </button>
                    </div>
                    {publishExtraEmails.length > 0 && (
                      <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginTop:6 }}>
                        {publishExtraEmails.map((e, i) => (
                          <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:3, padding:'3px 8px', borderRadius:14, fontSize:11, fontWeight:600, background:'rgba(6,182,212,0.06)', border:'1px solid rgba(6,182,212,0.15)', color:'var(--teal, #06B6D4)' }}>
                            {e}
                            <button onClick={() => setPublishExtraEmails(prev => prev.filter((_, j) => j !== i))}
                              style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(6,182,212,0.5)', fontSize:9, padding:0 }}><i className="fa-solid fa-xmark"></i></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer count */}
                  {totalCount > 0 && (
                    <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
                      <p style={{ fontSize:12, color:'var(--text2)', margin:0 }}>
                        <strong style={{ color:'var(--text)' }}>{totalCount}</strong> recipient{totalCount !== 1 ? 's' : ''}
                        {publishExtraEmails.length > 0 && <span style={{ color:'var(--teal, #06B6D4)', marginLeft:6, fontSize:11 }}>+{publishExtraEmails.length} one-off</span>}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Push notification toggle */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, marginBottom:12 }}>
              <i className="fa-solid fa-bell" style={{ fontSize:13, color:'#22c55e' }}></i>
              <span style={{ flex:1, fontSize:13, fontWeight:600, color:'var(--text2)' }}>Push notification to viewers</span>
              <button onClick={() => setPublishSendPush(prev => !prev)}
                style={{ padding:'5px 14px', borderRadius:16, fontSize:12, fontWeight:700, border:'none', cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s',
                  background: publishSendPush ? '#22c55e' : 'var(--bg)', color: publishSendPush ? '#fff' : 'var(--text3)' }}>
                {publishSendPush ? 'On' : 'Off'}
              </button>
            </div>

            {/* Action buttons */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
              <button onClick={async () => {
                setSaving(true);
                try {
                  const sheetId = await ensureSaved();
                  if (sheetId) {
                    await apiFetch(`/sheets/${sheetId}/publish`, { method:'POST', body: JSON.stringify({ emailGroupIds: selectedEmailGroups, emailExtraRecipients: publishExtraEmails, sendPush: publishSendPush }) });
                    await apiFetch(`/email/send/${sheetId}`, { method:'POST', body: JSON.stringify({ groupIds: selectedEmailGroups, extraEmails: publishExtraEmails }) });
                    setShowPreview(false);
                    navigate(`/admin?date=${date}&artist=${selArtist}`, { replace: true });
                  }
                } catch (e) {
                  alert('Error: ' + e.message);
                } finally {
                  setSaving(false);
                }
              }} disabled={saving||!selArtist||events.length===0||(selectedEmailGroups.length===0 && publishExtraEmails.length===0)}
                style={{ padding:'14px 10px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#1E40AF)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, boxShadow:'0 4px 12px rgba(30,64,175,0.25)', opacity:(saving||!selArtist||events.length===0||(selectedEmailGroups.length===0 && publishExtraEmails.length===0))?0.4:1 }}>
                <i className="fa-solid fa-paper-plane" style={{ fontSize:12 }}></i> Publish & Send Email
              </button>
              <button onClick={() => { handlePublish(); setShowPreview(false); }} disabled={saving||!selArtist||events.length===0}
                style={{ padding:'14px 10px', borderRadius:12, background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', color:'#fff', fontFamily:'inherit', fontSize:13, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, opacity:(saving||!selArtist||events.length===0)?0.5:1, boxShadow:'0 4px 12px rgba(34,197,94,0.25)' }}>
                <i className="fa-solid fa-rocket" style={{ fontSize:12 }}></i> Publish Only
              </button>
            </div>

            {/* Secondary actions */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:8 }}>
              <button onClick={() => { handleSaveDraft(); setShowPreview(false); }} disabled={saving||!selArtist} style={{ padding:'10px 8px', borderRadius:10, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                <i className="fa-solid fa-save" style={{ fontSize:11 }}></i> Draft
              </button>
              <button onClick={async () => {
                const sheetId = await ensureSaved();
                if (sheetId) { const API = process.env.REACT_APP_API_URL || 'http://localhost:3001'; window.open(`${API}/email/pdf/${sheetId}?token=${localStorage.getItem('ds_token')}`, '_blank'); }
              }} disabled={saving} style={{ padding:'10px 8px', borderRadius:10, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                <i className="fa-solid fa-file-pdf" style={{ fontSize:11, color:'#ef4444' }}></i> Email / PDF Preview
              </button>
              <button onClick={() => setShowPreview(false)} style={{ padding:'10px 8px', borderRadius:10, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:11, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                <i className="fa-solid fa-pen-to-square" style={{ fontSize:11 }}></i> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New review-and-send modal (replaces the inline distribution panel
          for the primary publish CTA). Closes after a successful send and
          navigates back to the admin home for the same date+artist. */}
      {showReviewModal && reviewSheet && (
        <ReviewAndSendModal
          sheet={reviewSheet}
          onClose={() => setShowReviewModal(false)}
          onSent={() => {
            setShowReviewModal(false);
            navigate(`/admin?date=${date}&artist=${selArtist}`, { replace: true });
          }}
        />
      )}
    </div>
  );
}
